/**
 * LeadershipReportsPage Component
 * 
 * FULLY ANONYMIZED sentiment and trends report for senior leadership.
 * This page generates and displays AI-powered analysis that contains:
 * - NO employee names or identifiable information
 * - High-level sentiment and morale indicators
 * - Strategic recommendations for leadership
 * - Risk areas and opportunities
 * 
 * Uses a separate backend endpoint that generates anonymized content.
 */

import React, { useState, useEffect, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import {
    Building2, Globe, BarChart3, Target, Shield, Lock,
    RefreshCw, Download, FileText, Loader2, HeartPulse,
    Star, AlertTriangle, Search, Link, MessageSquare,
    Calendar, CheckCircle2, Check, Clock, DollarSign,
    TrendingUp, Briefcase, Zap, Lightbulb, ClipboardList,
    ChevronRight, Users, Activity, Eye, ArrowUpRight,
    ArrowDownRight, Minus, LayoutDashboard, ListChecks,
    Heart, Hourglass, Pencil, Files, Ruler, Tag, Brain,
    User, FolderOpen, TrendingDown, ArrowRight,
    Smile, Scale, Settings, Pin, Share2, Copy, Trash2, ExternalLink, Mail,
    Sparkles, UserCheck, X, CalendarClock
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import StrategicTimelineView from './leadership/StrategicTimelineView';
import AutomationOutsourceView from './leadership/AutomationOutsourceView';
import RetentionRiskView from './leadership/RetentionRiskView';
import OrgDesignView from './leadership/OrgDesignView';
import PulseFeedbackView from './leadership/PulseFeedbackView';
import HeadlinesCard from './leadership/HeadlinesCard';
import ReportBuilder from './leadership/ReportBuilder';
import DepartmentSummaryPage from './DepartmentSummaryPage';
import '../styles/design-system.css';
import '../styles/reports-page.css';
import '../styles/leadership-reports.css';

/**
 * Get auth headers for API calls
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

const getApiErrorMessage = (body, fallback) => {
    const detail = body?.detail || body?.message;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (detail?.error) return detail.error;
    return fallback;
};

const readApiErrorMessage = async (response, fallback) => {
    const body = await response.json().catch(() => ({}));
    return getApiErrorMessage(body, fallback);
};

/**
 * Sentiment Badge Component - color-coded status indicators
 */
const SentimentBadge = ({ value, type = 'sentiment' }) => {
    const getColor = () => {
        const v = (value || '').toLowerCase();
        
        // Handle compound values like "Low-Moderate" or "Moderate-High"
        // Check for the more significant part first
        
        if (type === 'sentiment') {
            // Positive indicators
            if (v.includes('positive') || v.includes('good') || v.includes('high') || 
                v.includes('strong') || v.includes('excellent') || v.includes('great')) {
                return '#34C759';
            }
            // Neutral/Mixed indicators
            if (v.includes('mixed') || v.includes('moderate') || v.includes('medium') ||
                v.includes('neutral') || v.includes('adequate') || v.includes('satisfactory') ||
                v.includes('fair') || v.includes('average') || v.includes('okay')) {
                return '#FF9500';
            }
            // Negative indicators
            if (v.includes('concerning') || v.includes('negative') || v.includes('low') || 
                v.includes('poor') || v.includes('weak') || v.includes('lacking') ||
                v.includes('insufficient') || v.includes('frustrated')) {
                return '#FF3B30';
            }
        }
        
        if (type === 'risk') {
            // High risk (check first since "low-moderate" contains "low")
            if (v.includes('high') || v.includes('elevated') || v.includes('critical') || 
                v.includes('severe') || v.includes('significant')) {
                return '#FF3B30';
            }
            // Moderate risk
            if (v.includes('moderate') || v.includes('medium') || v.includes('some') ||
                v.includes('potential') || v.includes('emerging')) {
                return '#FF9500';
            }
            // Low risk (check last since other values might contain "low")
            if (v.includes('low') || v.includes('minimal') || v.includes('none') || 
                v.includes('negligible')) {
                return '#34C759';
            }
        }
        
        if (type === 'priority') {
            if (v.includes('immediate') || v.includes('high') || v.includes('urgent') ||
                v.includes('critical')) {
                return '#FF3B30';
            }
            if (v.includes('near-term') || v.includes('medium') || v.includes('moderate') ||
                v.includes('soon')) {
                return '#FF9500';
            }
            if (v.includes('long-term') || v.includes('low') || v.includes('future') ||
                v.includes('eventually')) {
                return '#007AFF';
            }
        }
        
        return '#86868B';
    };
    
    return (
        <span 
            className="leadership-badge"
            style={{ backgroundColor: getColor(), color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}
        >
            {value?.toString().replace(/_/g, ' ') || 'N/A'}
        </span>
    );
};

/**
 * Section wrapper with print support
 */
const ReportSection = ({ title, icon, children, className = '', id }) => (
    <section className={`rp-section ${className}`} id={id || undefined}>
        <h2 className="rp-section-title">
            {icon && <span className="rp-section-icon">{icon}</span>}
            {title}
        </h2>
        <div className="rp-section-content">
            {children}
        </div>
    </section>
);

/**
 * Executive TLDR Component
 * A concise one-page summary for executives with links to full report sections.
 * Extracts the most critical data points from the org-wide report.
 */
function ExecutiveTLDR({ orgReport, onViewFullReport, onScrollToSection }) {
    const analysis = orgReport?.analysis;
    if (!analysis) return null;

    const deptsIncluded = orgReport?.departments_included || [];
    const totalRespondents = orgReport?.total_respondents || 0;
    const deptsCount = orgReport?.departments_count || deptsIncluded.length;
    const health = analysis.organization_health_summary || {};
    const comparison = analysis.department_comparison || {};

    // Get top critical/high systemic patterns (max 3)
    const criticalPatterns = (analysis.systemic_patterns || [])
        .filter(p => p.severity === 'critical' || p.severity === 'high')
        .slice(0, 3);

    // Get #1 strategic priority
    const topPriority = (analysis.strategic_priorities || [])
        .sort((a, b) => (a.rank || 99) - (b.rank || 99))[0];

    // Get highest severity risk
    const topRisk = (analysis.cross_department_risks || [])
        .sort((a, b) => {
            const order = { critical: 0, high: 1, moderate: 2, low: 3 };
            return (order[a.severity] ?? 4) - (order[b.severity] ?? 4);
        })[0];

    // Get top shared pain point
    const topPainPoint = (analysis.shared_pain_points || [])[0];

    // 90-day outlook
    const outlook = analysis['90_day_organization_outlook'];

    const scrollTo = (sectionId) => {
        const el = document.getElementById(sectionId);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (onScrollToSection) onScrollToSection(sectionId);
    };

    return (
        <div className="exec-tldr">
            {/* TLDR Header */}
            <div className="exec-tldr-header">
                <div className="exec-tldr-title-row">
                    <h2><ClipboardList size={16} /> Executive Summary — TLDR</h2>
                    <span className="exec-tldr-badge">Quick Read</span>
                </div>
                <p className="exec-tldr-subtitle">
                    {deptsCount} departments &bull; {totalRespondents} respondents &bull; {deptsIncluded.join(', ')}
                </p>
                {orgReport?.generated_at && (
                    <p className="exec-tldr-date">
                        Report generated {new Date(orgReport.generated_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                        })}
                    </p>
                )}
            </div>

            {/* Organization Health At-a-Glance */}
            <div className="exec-tldr-section">
                <div className="exec-tldr-section-header">
                    <h3><Heart size={16} style={{ color: 'var(--color-success)' }} /> Organization Health</h3>
                    <button className="exec-tldr-link" onClick={() => scrollTo('org-section-health')}>
                        View details →
                    </button>
                </div>
                <div className="exec-tldr-health-grid">
                    <div className="exec-tldr-health-card primary">
                        <span className="exec-tldr-health-label">Overall</span>
                        <SentimentBadge value={health.overall_health_rating} type="sentiment" />
                    </div>
                    <div className="exec-tldr-health-card">
                        <span className="exec-tldr-health-label">Trajectory</span>
                        <SentimentBadge value={health.trajectory} type="sentiment" />
                    </div>
                    <div className="exec-tldr-health-card positive">
                        <span className="exec-tldr-health-label">Strongest</span>
                        <span className="exec-tldr-health-value">{health.strongest_area || '—'}</span>
                    </div>
                    <div className="exec-tldr-health-card concern">
                        <span className="exec-tldr-health-label">Needs Attention</span>
                        <span className="exec-tldr-health-value">{health.weakest_area || '—'}</span>
                    </div>
                </div>
            </div>

            {/* Department Spotlight */}
            {(comparison.healthiest_department || comparison.most_at_risk_department) && (
                <div className="exec-tldr-section">
                    <div className="exec-tldr-section-header">
                        <h3><BarChart3 size={16} /> Department Spotlight</h3>
                        <button className="exec-tldr-link" onClick={() => scrollTo('org-section-comparison')}>
                            View comparison →
                        </button>
                    </div>
                    <div className="exec-tldr-spotlight-row">
                        {comparison.healthiest_department && (
                            <div className="exec-tldr-spotlight positive">
                                <div className="exec-tldr-spotlight-label"><Star size={16} /> Healthiest</div>
                                <div className="exec-tldr-spotlight-dept">{comparison.healthiest_department.department}</div>
                                <p>{comparison.healthiest_department.strengths}</p>
                            </div>
                        )}
                        {comparison.most_at_risk_department && (
                            <div className="exec-tldr-spotlight concern">
                                <div className="exec-tldr-spotlight-label"><AlertTriangle size={16} /> Most At Risk</div>
                                <div className="exec-tldr-spotlight-dept">{comparison.most_at_risk_department.department}</div>
                                <p>{comparison.most_at_risk_department.concerns}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Critical Findings */}
            {criticalPatterns.length > 0 && (
                <div className="exec-tldr-section">
                    <div className="exec-tldr-section-header">
                        <h3><Search size={16} /> Critical Findings</h3>
                        <button className="exec-tldr-link" onClick={() => scrollTo('org-section-patterns')}>
                            View all patterns →
                        </button>
                    </div>
                    <div className="exec-tldr-findings">
                        {criticalPatterns.map((p, idx) => (
                            <div key={idx} className={`exec-tldr-finding severity-${p.severity}`}>
                                <div className="exec-tldr-finding-header">
                                    <SentimentBadge value={p.severity} type="risk" />
                                    <span className="exec-tldr-finding-type">{p.pattern_type}</span>
                                </div>
                                <h4>{p.pattern}</h4>
                                <p>{p.business_impact || p.description}</p>
                                <div className="exec-tldr-dept-pills">
                                    {(p.departments_affected || []).map((d, i) => (
                                        <span key={i} className="org-dept-pill">{d}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Top Priority + Top Risk side by side */}
            <div className="exec-tldr-two-col">
                {/* Top Strategic Priority */}
                {topPriority && (
                    <div className="exec-tldr-section exec-tldr-col">
                        <div className="exec-tldr-section-header">
                            <h3><Target size={16} /> #1 Priority</h3>
                            <button className="exec-tldr-link" onClick={() => scrollTo('org-section-priorities')}>
                                View all →
                            </button>
                        </div>
                        <div className="exec-tldr-priority-card">
                            <h4>{topPriority.priority}</h4>
                            <p>{topPriority.description}</p>
                            <div className="exec-tldr-priority-meta">
                                {topPriority.timeline && <span><Clock size={14} /> {topPriority.timeline}</span>}
                                {topPriority.investment_level && <span><DollarSign size={14} /> {topPriority.investment_level}</span>}
                                {topPriority.estimated_roi && <span><TrendingUp size={14} /> {topPriority.estimated_roi}</span>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Risk */}
                {topRisk && (
                    <div className="exec-tldr-section exec-tldr-col">
                        <div className="exec-tldr-section-header">
                            <h3><AlertTriangle size={16} /> Top Risk</h3>
                            <button className="exec-tldr-link" onClick={() => scrollTo('org-section-risks')}>
                                View all →
                            </button>
                        </div>
                        <div className={`exec-tldr-risk-card severity-${topRisk.severity}`}>
                            <div className="exec-tldr-risk-header">
                                <h4>{topRisk.risk}</h4>
                                <SentimentBadge value={topRisk.severity} type="risk" />
                            </div>
                            <p>{topRisk.description}</p>
                            {topRisk.recommended_action && (
                                <p className="exec-tldr-risk-action"><Target size={14} /> {topRisk.recommended_action}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Top Shared Pain Point */}
            {topPainPoint && (
                <div className="exec-tldr-section">
                    <div className="exec-tldr-section-header">
                        <h3><Link size={16} /> Top Shared Pain Point</h3>
                        <button className="exec-tldr-link" onClick={() => scrollTo('org-section-pain-points')}>
                            View all →
                        </button>
                    </div>
                    <div className="exec-tldr-pain-card">
                        <h4>{topPainPoint.pain_point}</h4>
                        <p>{topPainPoint.description}</p>
                        {topPainPoint.root_cause_analysis && (
                            <p className="exec-tldr-pain-root"><Search size={14} /> <strong>Root Cause:</strong> {topPainPoint.root_cause_analysis}</p>
                        )}
                        <div className="exec-tldr-dept-pills">
                            {(topPainPoint.departments_reporting || []).map((d, i) => (
                                <span key={i} className="org-dept-pill">{d}</span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* 90-Day Outlook Snapshot */}
            {outlook && (
                <div className="exec-tldr-section">
                    <div className="exec-tldr-section-header">
                        <h3><Calendar size={16} /> 90-Day Outlook</h3>
                        <button className="exec-tldr-link" onClick={() => scrollTo('org-section-outlook')}>
                            View full outlook →
                        </button>
                    </div>
                    <div className="exec-tldr-outlook">
                        <div className="exec-tldr-outlook-trajectory">
                            <span>Expected trajectory:</span>
                            <SentimentBadge value={outlook.expected_trajectory} type="sentiment" />
                        </div>
                        {outlook.success_indicators && (
                            <p className="exec-tldr-outlook-success"><CheckCircle2 size={14} /> <strong>Success looks like:</strong> {outlook.success_indicators}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Jump to Full Report Sections */}
            <div className="exec-tldr-nav">
                <h3><Files size={16} /> Full Report Sections</h3>
                <div className="exec-tldr-nav-grid">
                    {[
                        { id: 'org-section-executive-summary', label: 'Executive Summary', icon: <Pencil size={16} /> },
                        { id: 'org-section-health', label: 'Organization Health', icon: <Heart size={16} style={{ color: 'var(--color-success)' }} /> },
                        { id: 'org-section-comparison', label: 'Department Comparison', icon: <BarChart3 size={16} /> },
                        { id: 'org-section-patterns', label: 'Systemic Patterns', icon: <Search size={16} /> },
                        { id: 'org-section-pain-points', label: 'Shared Pain Points', icon: <Link size={16} /> },
                        { id: 'org-section-priorities', label: 'Strategic Priorities', icon: <Target size={16} /> },
                        { id: 'org-section-risks', label: 'Cross-Dept Risks', icon: <AlertTriangle size={16} /> },
                        { id: 'org-section-strengths', label: 'Dept Strengths', icon: <Star size={16} /> },
                        { id: 'org-section-outlook', label: '90-Day Outlook', icon: <Calendar size={16} /> },
                        { id: 'org-section-discussion', label: 'Discussion Questions', icon: <MessageSquare size={16} /> },
                    ].map(section => (
                        <button
                            key={section.id}
                            className="exec-tldr-nav-btn"
                            onClick={() => scrollTo(section.id)}
                        >
                            <span className="exec-tldr-nav-icon">{section.icon}</span>
                            <span>{section.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* View Full Report CTA */}
            <div className="exec-tldr-footer">
                <button className="exec-tldr-full-report-btn" onClick={onViewFullReport}>
                    <FileText size={16} /> Scroll to Full Report Below
                </button>
                <p className="exec-tldr-footer-note">
                    <Lock size={14} /> All data is anonymized. No employee names or identifiable information are included.
                </p>
            </div>
        </div>
    );
}

/**
 * Main Leadership Reports Page Component
 */
function LeadershipReportsPage({ reportGenerationLock }) {
    const [viewMode, setViewMode] = useState('overview'); // overview | organization | pulse | department | actionable | automation | timeline | retention | orgdesign | builder
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [orgTrends, setOrgTrends] = useState(null);
    const [deptTrends, setDeptTrends] = useState(null);
    const [sentimentReport, setSentimentReport] = useState(null);
    const [condensedSummary, setCondensedSummary] = useState(null);
    const [actionableReport, setActionableReport] = useState(null);
    const [orgReport, setOrgReport] = useState(null);
    const [loadingCondensed, setLoadingCondensed] = useState(false);
    const [loadingActionable, setLoadingActionable] = useState(false);
    const [loadingSentiment, setLoadingSentiment] = useState(false);
    const [loadingOrgReport, setLoadingOrgReport] = useState(false);
    const [orgProgress, setOrgProgress] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [showFullReport, setShowFullReport] = useState(false);
    const reportRef = useRef(null);

    // Share link state
    const [showSharePanel, setShowSharePanel] = useState(false);
    const [shareLinks, setShareLinks] = useState([]);
    const [loadingShareLinks, setLoadingShareLinks] = useState(false);
    const [generatingShareLink, setGeneratingShareLink] = useState(false);
    const [shareLinkLabel, setShareLinkLabel] = useState('');
    const [shareLinkExpiry, setShareLinkExpiry] = useState(30);
    const [shareRecipientEmails, setShareRecipientEmails] = useState('');
    const [shareLinkNotice, setShareLinkNotice] = useState('');
    const [shareLinkError, setShareLinkError] = useState('');
    const [revokingShareLinkId, setRevokingShareLinkId] = useState(null);
    const [copiedLinkId, setCopiedLinkId] = useState(null);
    const [showActionPlanSharePanel, setShowActionPlanSharePanel] = useState(false);
    const [actionPlanShareLinks, setActionPlanShareLinks] = useState([]);
    const [loadingActionPlanShareLinks, setLoadingActionPlanShareLinks] = useState(false);
    const [generatingActionPlanShareLink, setGeneratingActionPlanShareLink] = useState(false);
    const [actionPlanShareLinkLabel, setActionPlanShareLinkLabel] = useState('');
    const [actionPlanShareLinkExpiry, setActionPlanShareLinkExpiry] = useState(30);
    const [actionPlanShareRecipientEmails, setActionPlanShareRecipientEmails] = useState('');
    const [actionPlanShareLinkNotice, setActionPlanShareLinkNotice] = useState('');
    const [actionPlanShareLinkError, setActionPlanShareLinkError] = useState('');
    const [revokingActionPlanShareLinkId, setRevokingActionPlanShareLinkId] = useState(null);
    const [copiedActionPlanLinkId, setCopiedActionPlanLinkId] = useState(null);
    const [showDepartmentSummarySharePanel, setShowDepartmentSummarySharePanel] = useState(false);
    const [departmentSummaryShareLinks, setDepartmentSummaryShareLinks] = useState([]);
    const [loadingDepartmentSummaryShareLinks, setLoadingDepartmentSummaryShareLinks] = useState(false);
    const [generatingDepartmentSummaryShareLink, setGeneratingDepartmentSummaryShareLink] = useState(false);
    const [departmentSummaryShareLinkLabel, setDepartmentSummaryShareLinkLabel] = useState('');
    const [departmentSummaryShareLinkExpiry, setDepartmentSummaryShareLinkExpiry] = useState(90);
    const [departmentSummaryShareRecipientEmails, setDepartmentSummaryShareRecipientEmails] = useState('');
    const [departmentSummaryShareLinkNotice, setDepartmentSummaryShareLinkNotice] = useState('');
    const [departmentSummaryShareLinkError, setDepartmentSummaryShareLinkError] = useState('');
    const [revokingDepartmentSummaryShareLinkId, setRevokingDepartmentSummaryShareLinkId] = useState(null);
    const [copiedDepartmentSummaryLinkId, setCopiedDepartmentSummaryLinkId] = useState(null);
    // Recipient discovery + verification for the employee department summary
    const [departmentParticipants, setDepartmentParticipants] = useState([]);
    const [loadingDepartmentParticipants, setLoadingDepartmentParticipants] = useState(false);
    const [departmentParticipantsError, setDepartmentParticipantsError] = useState('');
    const [departmentParticipantsLoaded, setDepartmentParticipantsLoaded] = useState(false);
    const [selectedParticipantEmails, setSelectedParticipantEmails] = useState([]);
    // Preview of the employee-facing summary
    const [showDepartmentSummaryPreview, setShowDepartmentSummaryPreview] = useState(false);
    const [departmentSummaryPreviewData, setDepartmentSummaryPreviewData] = useState(null);
    const [loadingDepartmentSummaryPreview, setLoadingDepartmentSummaryPreview] = useState(false);
    const [departmentSummaryPreviewError, setDepartmentSummaryPreviewError] = useState('');
    const generationLocked = Boolean(reportGenerationLock?.locked);
    const generationLockMessage = reportGenerationLock?.message || 'Report generation is locked.';

    const parseShareRecipientEmails = (value) => {
        const entries = value
            .split(/[\n,;\s]+/)
            .map(email => email.trim().toLowerCase())
            .filter(Boolean);
        const uniqueEmails = [...new Set(entries)];
        const invalidEmails = uniqueEmails.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

        return { emails: uniqueEmails, invalidEmails };
    };

    // Fetch all share links
    const fetchShareLinks = async () => {
        try {
            setLoadingShareLinks(true);
            const res = await fetch(`${API_BASE_URL}/api/admin/leadership/share-links`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                setShareLinks(data);
            }
        } catch (err) {
            console.error('Failed to fetch share links:', err);
        } finally {
            setLoadingShareLinks(false);
        }
    };

    // Generate a new share link for a department
    const generateShareLink = async (dept) => {
        try {
            setGeneratingShareLink(true);
            setShareLinkNotice('');
            setShareLinkError('');

            const { emails, invalidEmails } = parseShareRecipientEmails(shareRecipientEmails);
            if (invalidEmails.length > 0) {
                setShareLinkError(`Check email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.slice(0, 3).join(', ')}`);
                return;
            }

            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/share`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        label: shareLinkLabel || null,
                        expires_days: shareLinkExpiry,
                        recipient_emails: emails
                    })
                }
            );
            if (res.ok) {
                const created = await res.json();
                setShareLinkLabel('');
                setShareRecipientEmails('');
                await fetchShareLinks();
                if (emails.length > 0) {
                    const sentCount = created.email_sent_count || 0;
                    const failedCount = created.email_failed_count || 0;
                    setShareLinkNotice(`Link created. ${sentCount} email${sentCount === 1 ? '' : 's'} sent${failedCount ? `, ${failedCount} failed` : ''}.`);
                    if (failedCount) {
                        setShareLinkError('Some emails were not sent. Check the recipient addresses or SendGrid configuration.');
                    }
                } else {
                    setShareLinkNotice('Link created. Copy it below or create another link with recipients.');
                }
            } else {
                const body = await res.json().catch(() => ({}));
                setShareLinkError(body.detail || 'Failed to generate share link.');
            }
        } catch (err) {
            console.error('Failed to generate share link:', err);
            setShareLinkError('Failed to generate share link.');
        } finally {
            setGeneratingShareLink(false);
        }
    };

    // Revoke a share link
    const revokeShareLink = async (linkId) => {
        if (!window.confirm('Delete this share link? It will stop working immediately.')) {
            return;
        }

        try {
            setRevokingShareLinkId(linkId);
            setShareLinkNotice('');
            setShareLinkError('');
            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/share-links/${linkId}`,
                { method: 'DELETE', headers: getAuthHeaders() }
            );
            if (res.ok) {
                setShareLinks(prev => prev.filter(link => link.id !== linkId));
                setShareLinkNotice('Share link deleted. It will no longer open the report.');
            } else {
                const body = await res.json().catch(() => ({}));
                setShareLinkError(body.detail || 'Failed to delete share link.');
            }
        } catch (err) {
            console.error('Failed to revoke share link:', err);
            setShareLinkError('Failed to delete share link.');
        } finally {
            setRevokingShareLinkId(null);
        }
    };

    // Copy share URL to clipboard
    const copyShareUrl = (token, linkId) => {
        const url = `${window.location.origin}/leadership/${token}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedLinkId(linkId);
            setTimeout(() => setCopiedLinkId(null), 2000);
        });
    };

    // Open share panel for a department
    const handleOpenSharePanel = () => {
        setShowDepartmentSummarySharePanel(false);
        setShowSharePanel(true);
        setShareLinkNotice('');
        setShareLinkError('');
        fetchShareLinks();
    };

    const fetchDepartmentSummaryShareLinks = async () => {
        try {
            setLoadingDepartmentSummaryShareLinks(true);
            const res = await fetch(`${API_BASE_URL}/api/admin/leadership/dept-summary/share-links`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                setDepartmentSummaryShareLinks(data);
            }
        } catch (err) {
            console.error('Failed to fetch department summary share links:', err);
        } finally {
            setLoadingDepartmentSummaryShareLinks(false);
        }
    };

    const generateDepartmentSummaryShareLink = async (dept) => {
        try {
            setGeneratingDepartmentSummaryShareLink(true);
            setDepartmentSummaryShareLinkNotice('');
            setDepartmentSummaryShareLinkError('');

            const { emails, invalidEmails } = parseShareRecipientEmails(departmentSummaryShareRecipientEmails);
            if (invalidEmails.length > 0) {
                setDepartmentSummaryShareLinkError(`Check email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.slice(0, 3).join(', ')}`);
                return;
            }
            if (emails.length === 0) {
                setDepartmentSummaryShareLinkError('Add at least one email recipient to send the invite.');
                return;
            }

            const expiresDays = Math.min(Number(departmentSummaryShareLinkExpiry) || 90, 90);
            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/dept-summary/share`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        label: departmentSummaryShareLinkLabel || null,
                        expires_days: expiresDays,
                        recipient_emails: emails
                    })
                }
            );
            if (res.ok) {
                const created = await res.json();
                setDepartmentSummaryShareLinkLabel('');
                setDepartmentSummaryShareRecipientEmails('');
                await fetchDepartmentSummaryShareLinks();
                const sentCount = created.email_sent_count || 0;
                const failedCount = created.email_failed_count || 0;
                setDepartmentSummaryShareLinkNotice(`Employee summary invite created. ${sentCount} email${sentCount === 1 ? '' : 's'} sent${failedCount ? `, ${failedCount} failed` : ''}.`);
                if (failedCount) {
                    setDepartmentSummaryShareLinkError('Some emails were not sent. Check the recipient addresses or SendGrid configuration.');
                }
            } else {
                const body = await res.json().catch(() => ({}));
                setDepartmentSummaryShareLinkError(body.detail || 'Failed to send employee summary invite.');
            }
        } catch (err) {
            console.error('Failed to generate department summary share link:', err);
            setDepartmentSummaryShareLinkError('Failed to send employee summary invite.');
        } finally {
            setGeneratingDepartmentSummaryShareLink(false);
        }
    };

    const revokeDepartmentSummaryShareLink = async (linkId) => {
        if (!window.confirm('Delete this employee summary link? It will stop working immediately.')) {
            return;
        }

        try {
            setRevokingDepartmentSummaryShareLinkId(linkId);
            setDepartmentSummaryShareLinkNotice('');
            setDepartmentSummaryShareLinkError('');
            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/dept-summary/share-links/${linkId}`,
                { method: 'DELETE', headers: getAuthHeaders() }
            );
            if (res.ok) {
                setDepartmentSummaryShareLinks(prev => prev.filter(link => link.id !== linkId));
                setDepartmentSummaryShareLinkNotice('Employee summary link deleted. It will no longer open the summary.');
            } else {
                const body = await res.json().catch(() => ({}));
                setDepartmentSummaryShareLinkError(body.detail || 'Failed to delete employee summary link.');
            }
        } catch (err) {
            console.error('Failed to revoke department summary share link:', err);
            setDepartmentSummaryShareLinkError('Failed to delete employee summary link.');
        } finally {
            setRevokingDepartmentSummaryShareLinkId(null);
        }
    };

    const copyDepartmentSummaryShareUrl = (token, linkId) => {
        const url = `${window.location.origin}/dept-summary/${token}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedDepartmentSummaryLinkId(linkId);
            setTimeout(() => setCopiedDepartmentSummaryLinkId(null), 2000);
        });
    };

    const handleOpenDepartmentSummarySharePanel = () => {
        setShowSharePanel(false);
        setShowDepartmentSummarySharePanel(true);
        setDepartmentSummaryShareLinkNotice('');
        setDepartmentSummaryShareLinkError('');
        setDepartmentParticipants([]);
        setDepartmentParticipantsError('');
        setDepartmentParticipantsLoaded(false);
        setSelectedParticipantEmails([]);
        fetchDepartmentSummaryShareLinks();
    };

    // Pull the survey participants for a department so the admin can verify recipients
    const fetchDepartmentParticipants = async (dept) => {
        try {
            setLoadingDepartmentParticipants(true);
            setDepartmentParticipantsError('');
            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/participants`,
                { headers: getAuthHeaders() }
            );
            if (res.ok) {
                const data = await res.json();
                const participants = data.participants || [];
                setDepartmentParticipants(participants);
                setDepartmentParticipantsLoaded(true);
                // Default selection: everyone who completed the survey
                const defaultSelected = participants
                    .filter(p => p.completed)
                    .map(p => p.email);
                const selected = defaultSelected.length > 0 ? defaultSelected : participants.map(p => p.email);
                setSelectedParticipantEmails(selected);
                setDepartmentSummaryShareRecipientEmails(selected.join(', '));
                if (participants.length === 0) {
                    setDepartmentParticipantsError('No survey participants found for this department.');
                }
            } else {
                const body = await res.json().catch(() => ({}));
                setDepartmentParticipantsError(body.detail || 'Failed to load department participants.');
            }
        } catch (err) {
            console.error('Failed to fetch department participants:', err);
            setDepartmentParticipantsError('Failed to load department participants.');
        } finally {
            setLoadingDepartmentParticipants(false);
        }
    };

    // Toggle a single participant in the verified recipient selection
    const toggleParticipantSelection = (email) => {
        setSelectedParticipantEmails(prev => {
            const next = prev.includes(email)
                ? prev.filter(e => e !== email)
                : [...prev, email];
            setDepartmentSummaryShareRecipientEmails(next.join(', '));
            return next;
        });
    };

    const setAllParticipantsSelected = (selectAll) => {
        const next = selectAll ? departmentParticipants.map(p => p.email) : [];
        setSelectedParticipantEmails(next);
        setDepartmentSummaryShareRecipientEmails(next.join(', '));
    };

    // Preview the exact employee-facing summary without creating a link
    const openDepartmentSummaryPreview = async (dept) => {
        try {
            setShowDepartmentSummaryPreview(true);
            setLoadingDepartmentSummaryPreview(true);
            setDepartmentSummaryPreviewError('');
            setDepartmentSummaryPreviewData(null);
            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/dept-summary/preview`,
                { headers: getAuthHeaders() }
            );
            if (res.ok) {
                setDepartmentSummaryPreviewData(await res.json());
            } else {
                const body = await res.json().catch(() => ({}));
                setDepartmentSummaryPreviewError(body.detail || 'Failed to load summary preview.');
            }
        } catch (err) {
            console.error('Failed to load department summary preview:', err);
            setDepartmentSummaryPreviewError('Failed to load summary preview.');
        } finally {
            setLoadingDepartmentSummaryPreview(false);
        }
    };

    const closeDepartmentSummaryPreview = () => {
        setShowDepartmentSummaryPreview(false);
        setDepartmentSummaryPreviewData(null);
        setDepartmentSummaryPreviewError('');
    };

    const fetchActionPlanShareLinks = async () => {
        try {
            setLoadingActionPlanShareLinks(true);
            const res = await fetch(`${API_BASE_URL}/api/admin/leadership/action-plan/share-links`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                setActionPlanShareLinks(data);
            }
        } catch (err) {
            console.error('Failed to fetch action plan share links:', err);
        } finally {
            setLoadingActionPlanShareLinks(false);
        }
    };

    const generateActionPlanShareLink = async (dept) => {
        try {
            setGeneratingActionPlanShareLink(true);
            setActionPlanShareLinkNotice('');
            setActionPlanShareLinkError('');

            const { emails, invalidEmails } = parseShareRecipientEmails(actionPlanShareRecipientEmails);
            if (invalidEmails.length > 0) {
                setActionPlanShareLinkError(`Check email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.slice(0, 3).join(', ')}`);
                return;
            }

            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/action-plan/share`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        label: actionPlanShareLinkLabel || null,
                        expires_days: actionPlanShareLinkExpiry,
                        recipient_emails: emails,
                        actionable_report: actionableReport || null
                    })
                }
            );
            if (res.ok) {
                const created = await res.json();
                setActionPlanShareLinkLabel('');
                setActionPlanShareRecipientEmails('');
                await fetchActionPlanShareLinks();
                if (emails.length > 0) {
                    const sentCount = created.email_sent_count || 0;
                    const failedCount = created.email_failed_count || 0;
                    setActionPlanShareLinkNotice(`Action roadmap link created. ${sentCount} email${sentCount === 1 ? '' : 's'} sent${failedCount ? `, ${failedCount} failed` : ''}.`);
                    if (failedCount) {
                        setActionPlanShareLinkError('Some emails were not sent. Check the recipient addresses or SendGrid configuration.');
                    }
                } else {
                    setActionPlanShareLinkNotice('Action roadmap link created. Copy it below or create another link with recipients.');
                }
            } else {
                const body = await res.json().catch(() => ({}));
                setActionPlanShareLinkError(body.detail || 'Failed to generate action roadmap link.');
            }
        } catch (err) {
            console.error('Failed to generate action plan share link:', err);
            setActionPlanShareLinkError('Failed to generate action roadmap link.');
        } finally {
            setGeneratingActionPlanShareLink(false);
        }
    };

    const revokeActionPlanShareLink = async (linkId) => {
        if (!window.confirm('Delete this action roadmap link? It will stop working immediately.')) {
            return;
        }

        try {
            setRevokingActionPlanShareLinkId(linkId);
            setActionPlanShareLinkNotice('');
            setActionPlanShareLinkError('');
            const res = await fetch(
                `${API_BASE_URL}/api/admin/leadership/action-plan/share-links/${linkId}`,
                { method: 'DELETE', headers: getAuthHeaders() }
            );
            if (res.ok) {
                setActionPlanShareLinks(prev => prev.filter(link => link.id !== linkId));
                setActionPlanShareLinkNotice('Action roadmap link deleted. It will no longer open the roadmap.');
            } else {
                const body = await res.json().catch(() => ({}));
                setActionPlanShareLinkError(body.detail || 'Failed to delete action roadmap link.');
            }
        } catch (err) {
            console.error('Failed to revoke action plan share link:', err);
            setActionPlanShareLinkError('Failed to delete action plan link.');
        } finally {
            setRevokingActionPlanShareLinkId(null);
        }
    };

    const copyActionPlanShareUrl = (token, linkId) => {
        const url = `${window.location.origin}/action-plan/${token}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedActionPlanLinkId(linkId);
            setTimeout(() => setCopiedActionPlanLinkId(null), 2000);
        });
    };

    const handleOpenActionPlanSharePanel = () => {
        setShowActionPlanSharePanel(true);
        setActionPlanShareLinkNotice('');
        setActionPlanShareLinkError('');
        fetchActionPlanShareLinks();
    };

    // Load initial data
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                
                const [deptsRes, trendsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/admin/departments`, { headers: getAuthHeaders() }),
                    fetch(`${API_BASE_URL}/api/admin/trends/all`, { headers: getAuthHeaders() })
                ]);
                
                if (!deptsRes.ok || !trendsRes.ok) {
                    throw new Error('Failed to load data');
                }
                
                const depts = await deptsRes.json();
                const trends = await trendsRes.json();
                
                setDepartments(depts);
                setOrgTrends(trends);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, []);

    // Load department-specific trends (basic stats only)
    const loadDeptTrends = async (dept) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(
                `${API_BASE_URL}/api/admin/trends/department/${encodeURIComponent(dept)}`,
                { headers: getAuthHeaders() }
            );
            
            if (!response.ok) {
                throw new Error('Failed to load department data');
            }
            
            const data = await response.json();
            setDeptTrends(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load ANONYMIZED leadership sentiment analysis - uses NEW endpoint
    const loadSentimentReport = async (dept, forceRefresh = false) => {
        try {
            setLoadingSentiment(true);
            setSentimentReport(null);

            if (forceRefresh && generationLocked) {
                setSentimentReport({ error: generationLockMessage });
                setLoadingSentiment(false);
                return;
            }
            
            // Use the NEW anonymized leadership sentiment endpoint
            const startUrl = `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/sentiment/start${forceRefresh ? '?force_refresh=true' : ''}`;
            const startResponse = await fetch(startUrl, { 
                method: 'POST',
                headers: getAuthHeaders()
            });
            
            if (!startResponse.ok) {
                throw new Error(await readApiErrorMessage(startResponse, 'Failed to generate sentiment report'));
            }
            
            const startData = await startResponse.json();
            
            if (startData.status === 'completed' && startData.result) {
                setSentimentReport(startData.result);
                setLoadingSentiment(false);
                // Refresh condensed summary when loading from cache
                loadCondensedSummary(dept);
                return;
            }
            
            if (startData.status === 'started' || startData.status === 'running') {
                await pollForSentimentResult(dept);
            }
            
        } catch (err) {
            console.error('Sentiment report error:', err);
            setSentimentReport({ error: err.message });
            setLoadingSentiment(false);
        }
    };
    
    // Poll for sentiment analysis results — polls by department (DB-backed, multi-worker safe)
    const pollForSentimentResult = async (dept) => {
        const maxAttempts = 120;
        const pollInterval = 5000;
        const maxConsecutiveErrors = 3;
        let consecutiveErrors = 0;
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const statusUrl = `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/sentiment/status`;
                const response = await fetch(statusUrl, { headers: getAuthHeaders() });
                
                if (!response.ok) {
                    throw new Error('Failed to check report status');
                }
                
                consecutiveErrors = 0;
                const data = await response.json();
                
                if (data.status === 'completed') {
                    setSentimentReport(data.result);
                    setLoadingSentiment(false);
                    // Refresh condensed summary after new report is generated
                    loadCondensedSummary(dept);
                    return;
                }
                
                if (data.status === 'error') {
                    setSentimentReport({ error: data.error || 'Report generation failed' });
                    setLoadingSentiment(false);
                    return;
                }
                
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                
            } catch (err) {
                consecutiveErrors++;
                console.error(`Poll error (${consecutiveErrors}/${maxConsecutiveErrors}):`, err);
                if (consecutiveErrors >= maxConsecutiveErrors) {
                    setSentimentReport({ error: 'Lost connection while generating report.' });
                    setLoadingSentiment(false);
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
        }
        
        setSentimentReport({ error: 'Report generation is taking longer than expected.' });
        setLoadingSentiment(false);
    };

    // Load condensed summary (quick highlights)
    const loadCondensedSummary = async (dept) => {
        try {
            setLoadingCondensed(true);
            setCondensedSummary(null);
            
            const response = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/sentiment/condensed`,
                { headers: getAuthHeaders() }
            );
            
            if (response.status === 404) {
                // No report yet - condensed not available
                setCondensedSummary(null);
            } else if (response.ok) {
                const data = await response.json();
                setCondensedSummary(data.condensed_summary);
            }
        } catch (err) {
            console.error('Condensed summary error:', err);
            setCondensedSummary(null);
        } finally {
            setLoadingCondensed(false);
        }
    };

    // Load actionable report (cached strategy-led action report)
    const loadActionableReport = async (dept) => {
        try {
            setLoadingActionable(true);
            setActionableReport(null);

            const response = await fetch(
                `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/actionable`,
                { headers: getAuthHeaders() }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'not_generated' || !data.actionable_report) {
                    // No strategy-led report yet — show the Generate prompt
                    setActionableReport({ needsSentimentReport: true });
                } else {
                    console.log('Strategy-led action report loaded:', data);
                    setActionableReport(data.actionable_report);
                }
            } else if (response.status === 404) {
                setActionableReport({ needsSentimentReport: true });
            } else {
                setActionableReport({ error: await readApiErrorMessage(response, 'Failed to load action items') });
            }
        } catch (err) {
            console.error('Actionable report error:', err);
            setActionableReport({ error: err.message || 'Failed to load action items' });
        } finally {
            setLoadingActionable(false);
        }
    };

    // Generate (or regenerate) the strategy-led action report: async + poll DB status
    const generateActionableReport = async (dept, forceRefresh = false) => {
        if (generationLocked) {
            setActionableReport({ error: generationLockMessage });
            return;
        }
        try {
            setLoadingActionable(true);
            setActionableReport(null);

            const startUrl = `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/action-report/start${forceRefresh ? '?force_refresh=true' : ''}`;
            const startRes = await fetch(startUrl, { method: 'POST', headers: getAuthHeaders() });

            if (!startRes.ok) {
                throw new Error(await readApiErrorMessage(startRes, 'Failed to start action report'));
            }

            const startData = await startRes.json();

            if (startData.status === 'completed' && startData.result) {
                setActionableReport(startData.result.actionable_report);
                setLoadingActionable(false);
                return;
            }

            if (startData.status === 'started' || startData.status === 'running') {
                await pollForActionableReport(dept);
            }
        } catch (err) {
            console.error('Action report generation error:', err);
            setActionableReport({ error: err.message || 'Failed to generate action report' });
            setLoadingActionable(false);
        }
    };

    // Poll DB-backed status for the strategy-led action report (multi-worker safe)
    const pollForActionableReport = async (dept) => {
        const maxAttempts = 240;
        const pollInterval = 5000;
        const maxConsecutiveErrors = 3;
        let consecutiveErrors = 0;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/action-report/status`,
                    { headers: getAuthHeaders() }
                );
                if (!res.ok) throw new Error('Failed to check action report status');

                consecutiveErrors = 0;
                const data = await res.json();

                if (data.status === 'completed' && data.result) {
                    setActionableReport(data.result.actionable_report);
                    setLoadingActionable(false);
                    return;
                }
                if (data.status === 'error') {
                    setActionableReport({ error: data.error || 'Action report generation failed' });
                    setLoadingActionable(false);
                    return;
                }

                await new Promise(resolve => setTimeout(resolve, pollInterval));
            } catch (err) {
                consecutiveErrors++;
                console.error(`Action report poll error (${consecutiveErrors}/${maxConsecutiveErrors}):`, err);
                if (consecutiveErrors >= maxConsecutiveErrors) {
                    setActionableReport({ error: 'Lost connection while generating action report.' });
                    setLoadingActionable(false);
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
        }

        setActionableReport({ error: 'Action report generation timed out.' });
        setLoadingActionable(false);
    };

    // Handle department selection
    const handleSelectDepartment = (dept) => {
        setSelectedDepartment(dept);
        setViewMode('department');
        setSentimentReport(null);
        setCondensedSummary(null);
        setActionableReport(null);
        setShowFullReport(false);
        loadDeptTrends(dept);
        // Load condensed first - it's faster since it doesn't require AI generation
        // Only load full report after condensed (or if condensed fails)
        loadCondensedSummary(dept).then(() => {
            loadSentimentReport(dept);
            loadActionableReport(dept);
        });
    };

    // Switch to actionable report view
    const handleViewActionable = () => {
        setViewMode('actionable');
        if (!actionableReport && selectedDepartment) {
            loadActionableReport(selectedDepartment);
        }
    };

    // Handle department selection specifically for actionable view
    const handleSelectDepartmentForActionable = (dept) => {
        setSelectedDepartment(dept);
        setViewMode('actionable');
        setActionableReport(null);
        loadActionableReport(dept);
    };

    // Load organization-wide report
    const loadOrgReport = async (forceRefresh = false) => {
        try {
            setLoadingOrgReport(true);
            setOrgReport(null);

            if (forceRefresh && generationLocked) {
                setOrgReport({ error: generationLockMessage });
                setLoadingOrgReport(false);
                return;
            }
            
            // Try to load existing report first (unless forcing refresh)
            if (!forceRefresh) {
                try {
                    const existingRes = await fetch(
                        `${API_BASE_URL}/api/admin/leadership/organization/report`,
                        { headers: getAuthHeaders() }
                    );
                    if (existingRes.ok) {
                        const data = await existingRes.json();
                        setOrgReport(data);
                        setLoadingOrgReport(false);
                        return;
                    }
                } catch (e) {
                    // No cached report — proceed to generate
                }
            }
            
            // Start generation
            const startUrl = `${API_BASE_URL}/api/admin/leadership/organization/report/start${forceRefresh ? '?force_refresh=true' : ''}`;
            const startRes = await fetch(startUrl, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            
            if (!startRes.ok) {
                throw new Error(await readApiErrorMessage(startRes, 'Failed to start organization report'));
            }
            
            const startData = await startRes.json();
            
            if (startData.status === 'completed' && startData.result) {
                setOrgReport(startData.result);
                setLoadingOrgReport(false);
                return;
            }
            
            if (startData.status === 'started' || startData.status === 'running') {
                await pollForOrgReport();
            }
        } catch (err) {
            console.error('Org report error:', err);
            setOrgReport({ error: err.message });
            setLoadingOrgReport(false);
        }
    };
    
    // Poll for org report results — polls DB status (no job_id needed, multi-worker safe)
    const pollForOrgReport = async () => {
        const maxAttempts = 240;
        const pollInterval = 5000;
        const maxConsecutiveErrors = 3;
        let consecutiveErrors = 0;
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/admin/leadership/organization/report/status`,
                    { headers: getAuthHeaders() }
                );
                if (!res.ok) throw new Error('Failed to check org report status');
                
                consecutiveErrors = 0;
                const data = await res.json();
                
                if (data.status === 'completed') {
                    setOrgReport(data.result);
                    setOrgProgress('');
                    setLoadingOrgReport(false);
                    return;
                }
                if (data.status === 'error') {
                    setOrgReport({ error: data.error || 'Report generation failed' });
                    setOrgProgress('');
                    setLoadingOrgReport(false);
                    return;
                }
                if (data.status === 'running' && data.message) {
                    setOrgProgress(data.message);
                }
                
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            } catch (err) {
                consecutiveErrors++;
                console.error(`Org poll error (${consecutiveErrors}/${maxConsecutiveErrors}):`, err);
                if (consecutiveErrors >= maxConsecutiveErrors) {
                    setOrgReport({ error: 'Lost connection while generating report.' });
                    setOrgProgress('');
                    setLoadingOrgReport(false);
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
        }
        
        setOrgReport({ error: 'Report generation timed out.' });
        setOrgProgress('');
        setLoadingOrgReport(false);
    };

    // Handle regenerating sentiment report
    const handleRegenerateSentiment = () => {
        if (selectedDepartment) {
            if (generationLocked) {
                setSentimentReport({ error: generationLockMessage });
                return;
            }
            setCondensedSummary(null);
            setShowFullReport(false);
            loadSentimentReport(selectedDepartment, true);
        }
    };

    // Export to PDF
    const handleExport = async () => {
        if (!reportRef.current) return;
        
        setExporting(true);
        
        try {
            const reportTitle = viewMode === 'overview' 
                ? 'Leadership-Sentiment-Report' 
                : `${selectedDepartment}-Leadership-Sentiment`;
            const filename = `${reportTitle}-${new Date().toISOString().split('T')[0]}.pdf`;
            
            const element = reportRef.current;
            
            const options = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: filename,
                image: { type: 'jpeg', quality: 0.95 },
                html2canvas: { 
                    scale: 2,
                    useCORS: true,
                    letterRendering: true,
                    scrollX: 0,
                    scrollY: -window.scrollY,
                    backgroundColor: '#ffffff',
                    onclone: (clonedDoc) => {
                        clonedDoc.querySelectorAll('.no-print').forEach(el => el.remove());
                        clonedDoc.querySelectorAll('.print-only').forEach(el => {
                            el.style.display = 'block';
                        });
                        clonedDoc.querySelectorAll('.rp-section').forEach(section => {
                            section.style.boxShadow = 'none';
                            section.style.border = '1px solid #ddd';
                            section.style.pageBreakInside = 'avoid';
                        });
                    }
                },
                jsPDF: { 
                    unit: 'in', 
                    format: 'letter', 
                    orientation: 'portrait',
                    compress: true
                },
                pagebreak: { 
                    mode: ['css', 'legacy'],
                    avoid: ['.leadership-card', '.rp-section']
                }
            };
            
            await html2pdf().set(options).from(element).save();
            
        } catch (err) {
            console.error('PDF export error:', err);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    // Render loading state
    if (loading && !orgTrends && !deptTrends) {
        return (
            <div className="rp-container leadership-view">
                <div className="rp-loading">
                    <div className="rp-spinner"></div>
                    <p>Loading leadership reports...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rp-container leadership-view">
            {/* Navigation Header */}
            <header className="rp-header no-print">
                <div className="rp-header-left">
                    <h1 className="rp-title">
                        <Shield size={20} style={{ marginRight: 8 }} />
                        {viewMode === 'overview' ? 'Leadership Dashboard' 
                           : viewMode === 'organization' ? 'Organization-Wide Report'
                           : viewMode === 'actionable' ? (selectedDepartment ? `${selectedDepartment} Action Roadmap` : 'Action Roadmap')
                           : viewMode === 'retention' ? (selectedDepartment ? `${selectedDepartment} Retention Risk` : 'Organization Retention Risk')
                           : viewMode === 'automation' ? (selectedDepartment ? `${selectedDepartment} Automation & Outsource` : 'Organization Automation & Outsource')
                           : viewMode === 'timeline' ? (selectedDepartment ? `${selectedDepartment} Strategic Timeline` : 'Organization Strategic Timeline')
                           : viewMode === 'orgdesign' ? 'Organization Design'
                           : viewMode === 'pulse' ? 'Pulse Feedback'
                           : viewMode === 'builder' ? 'Report Builder'
                           : selectedDepartment ? `${selectedDepartment} Sentiment Report` 
                           : 'Leadership Dashboard'}
                    </h1>
                </div>
                <div className="rp-header-actions">
                    {/* Regenerate for dept-level views when sentiment data exists */}
                    {(['department', 'retention', 'automation', 'timeline'].includes(viewMode)) && selectedDepartment && (sentimentReport?.analysis || sentimentReport?.error) && (
                        <button 
                            className="rp-btn rp-btn-secondary"
                            onClick={handleRegenerateSentiment}
                            disabled={generationLocked || loadingSentiment}
                        >
                            <RefreshCw size={14} /> {loadingSentiment ? 'Regenerating...' : 'Regenerate Report'}
                        </button>
                    )}
                    {/* Regenerate the strategy-led action report */}
                    {viewMode === 'actionable' && selectedDepartment && actionableReport && !actionableReport.needsSentimentReport && (
                        <button
                            className="rp-btn rp-btn-secondary"
                            onClick={() => generateActionableReport(selectedDepartment, true)}
                            disabled={generationLocked || loadingActionable}
                            title="Regenerate from latest survey data and the 5-year plan"
                        >
                            <RefreshCw size={14} /> {loadingActionable ? 'Regenerating...' : 'Regenerate Action Roadmap'}
                        </button>
                    )}
                    {/* Regenerate for org-level views */}
                    {(['retention', 'automation', 'timeline', 'orgdesign'].includes(viewMode)) && !selectedDepartment && (orgReport?.analysis || orgReport?.error) && (
                        <button 
                            className="rp-btn rp-btn-secondary"
                            onClick={() => loadOrgReport(true)}
                            disabled={generationLocked || loadingOrgReport}
                        >
                            <RefreshCw size={14} /> {loadingOrgReport ? 'Regenerating...' : 'Regenerate Org Report'}
                        </button>
                    )}
                    <button
                        className="rp-btn rp-btn-secondary" 
                        onClick={handleExport}
                        disabled={exporting}
                    >
                        {exporting ? <><Hourglass size={14} /> Generating PDF...</> : <><FileText size={14} /> Export PDF</>}
                    </button>
                </div>
            </header>

            {/* Anonymous Mode Banner */}
            <div className="leadership-banner">
                <div className="leadership-banner-content">
                    <span className="leadership-banner-icon"><Lock size={16} /></span>
                    <div className="leadership-banner-text">
                        <strong>Anonymized Leadership Report</strong>
                        <span>This report contains NO employee names or identifiable information. All insights are aggregated for privacy.</span>
                    </div>
                </div>
            </div>

            {generationLocked && (
                <div className="leadership-banner" style={{ borderColor: 'var(--color-warning)', background: 'var(--color-warning-light)' }}>
                    <div className="leadership-banner-content">
                        <span className="leadership-banner-icon"><Lock size={16} /></span>
                        <div className="leadership-banner-text">
                            <strong>Report Generation Locked</strong>
                            <span>{generationLockMessage}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* View Toggle & Department Selection — grouped nav (Apr 21 cleanup) */}
            <nav className="rp-nav rp-nav-grouped no-print">
                {/* Featured action: Report Builder */}
                <div className="rp-nav-featured">
                    <button
                        className={`rp-nav-builder-btn ${viewMode === 'builder' ? 'active' : ''}`}
                        onClick={() => setViewMode('builder')}
                        title="Build a custom report from any combination of sections"
                    >
                        <Sparkles size={14} /> Report Builder
                        <span className="rp-nav-featured-tag">Custom</span>
                    </button>
                </div>

                {/* Grouped tabs */}
                <div className="rp-nav-groups">
                    {/* GROUP 1: Snapshots — high-level views */}
                    <div className="rp-nav-group">
                        <span className="rp-nav-group-label">Snapshots</span>
                        <div className="rp-nav-group-tabs">
                            <button
                                className={`rp-nav-tab ${viewMode === 'overview' ? 'active' : ''}`}
                                onClick={() => {
                                    setViewMode('overview');
                                    setSelectedDepartment(null);
                                    setDeptTrends(null);
                                    setSentimentReport(null);
                                }}
                            >
                                <Building2 size={14} /> Overview
                            </button>
                            <button
                                className={`rp-nav-tab ${viewMode === 'organization' ? 'active' : ''}`}
                                onClick={() => {
                                    setViewMode('organization');
                                    if (!orgReport) loadOrgReport();
                                }}
                            >
                                <Globe size={14} /> Org Report
                            </button>
                            <button
                                className={`rp-nav-tab ${viewMode === 'pulse' ? 'active' : ''}`}
                                onClick={() => setViewMode('pulse')}
                            >
                                <MessageSquare size={14} /> Pulse Feedback
                            </button>
                        </div>
                    </div>

                    {/* GROUP 2: Department Deep Dive */}
                    <div className="rp-nav-group">
                        <span className="rp-nav-group-label">Department Deep Dive</span>
                        <div className="rp-nav-group-tabs">
                            <button
                                className={`rp-nav-tab ${viewMode === 'department' ? 'active' : ''}`}
                                onClick={() => setViewMode('department')}
                            >
                                <BarChart3 size={14} /> Sentiment
                            </button>
                            <button
                                className={`rp-nav-tab ${viewMode === 'actionable' ? 'active' : ''}`}
                                onClick={handleViewActionable}
                            >
                                <Target size={14} /> Action Roadmap
                            </button>
                        </div>
                    </div>

                    {/* GROUP 3: Strategic Lenses */}
                    <div className="rp-nav-group">
                        <span className="rp-nav-group-label">Strategic Lenses</span>
                        <div className="rp-nav-group-tabs">
                            <button
                                className={`rp-nav-tab ${viewMode === 'automation' ? 'active' : ''}`}
                                onClick={() => {
                                    setViewMode('automation');
                                    if (selectedDepartment && !sentimentReport) loadSentimentReport(selectedDepartment);
                                    if (!selectedDepartment && !orgReport) loadOrgReport();
                                }}
                            >
                                <Zap size={14} /> Automation
                            </button>
                            <button
                                className={`rp-nav-tab ${viewMode === 'timeline' ? 'active' : ''}`}
                                onClick={() => {
                                    setViewMode('timeline');
                                    if (selectedDepartment && !sentimentReport) loadSentimentReport(selectedDepartment);
                                    if (!selectedDepartment && !orgReport) loadOrgReport();
                                }}
                            >
                                <Calendar size={14} /> Timeline
                            </button>
                            {/* Retention Risk: org-only per Apr 20 review */}
                            {!selectedDepartment && (
                                <button
                                    className={`rp-nav-tab ${viewMode === 'retention' ? 'active' : ''}`}
                                    onClick={() => {
                                        setViewMode('retention');
                                        if (!orgReport) loadOrgReport();
                                    }}
                                >
                                    <Heart size={14} /> Retention
                                </button>
                            )}
                            <button
                                className={`rp-nav-tab ${viewMode === 'orgdesign' ? 'active' : ''}`}
                                onClick={() => {
                                    setViewMode('orgdesign');
                                    if (!orgReport) loadOrgReport();
                                }}
                            >
                                <Users size={14} /> Org Design
                            </button>
                        </div>
                    </div>
                </div>

                {/* Department selector — only shows for views that need it */}
                {(viewMode === 'department' || viewMode === 'actionable' || viewMode === 'retention' || viewMode === 'automation' || viewMode === 'timeline') && (
                    <div className="rp-dept-selector">
                        <select
                            value={selectedDepartment || ''}
                            onChange={(e) => {
                                const dept = e.target.value;
                                if (!dept) {
                                    setSelectedDepartment(null);
                                    setSentimentReport(null);
                                    if (!orgReport) loadOrgReport();
                                    return;
                                }
                                if (viewMode === 'actionable') {
                                    handleSelectDepartmentForActionable(dept);
                                } else if (viewMode === 'department') {
                                    handleSelectDepartment(dept);
                                } else if (viewMode === 'retention') {
                                    handleSelectDepartment(dept);
                                } else {
                                    setSelectedDepartment(dept);
                                    setSentimentReport(null);
                                    loadSentimentReport(dept);
                                    loadDeptTrends(dept);
                                }
                            }}
                        >
                            <option value="">{viewMode === 'retention' || viewMode === 'automation' || viewMode === 'timeline' ? 'Org-wide (or pick a department)…' : 'Select a department…'}</option>
                            {departments.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>
                    </div>
                )}
            </nav>

            {error && <div className="rp-error no-print">{error}</div>}

            {/* Main Report Content */}
            <main className="rp-content" ref={reportRef}>
                {/* Print Header */}
                <div className="rp-print-header print-only">
                    <h1>{viewMode === 'overview' ? 'Organization Leadership Report' : `${selectedDepartment} Sentiment Report`}</h1>
                    <p className="rp-print-subtitle">Anonymized Analysis - No Individual Data</p>
                    <p className="rp-print-date">Generated: {new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    })}</p>
                </div>

                {viewMode === 'overview' && orgTrends && (
                    <OrganizationOverview trends={orgTrends} onSelectDepartment={handleSelectDepartment} />
                )}

                {viewMode === 'organization' && (
                    <OrganizationReportView 
                        orgReport={orgReport}
                        loadingOrgReport={loadingOrgReport}
                        orgProgress={orgProgress}
                        departments={departments}
                        onGenerate={() => loadOrgReport(false)}
                        onRegenerate={() => loadOrgReport(true)}
                        generationLocked={generationLocked}
                        generationLockMessage={generationLockMessage}
                        showSharePanel={showActionPlanSharePanel}
                        onToggleSharePanel={() => showActionPlanSharePanel ? setShowActionPlanSharePanel(false) : handleOpenActionPlanSharePanel()}
                        shareLinks={actionPlanShareLinks.filter(link => link.department === selectedDepartment && !link.is_revoked)}
                        loadingShareLinks={loadingActionPlanShareLinks}
                        generatingShareLink={generatingActionPlanShareLink}
                        shareLinkLabel={actionPlanShareLinkLabel}
                        onShareLinkLabelChange={setActionPlanShareLinkLabel}
                        shareLinkExpiry={actionPlanShareLinkExpiry}
                        onShareLinkExpiryChange={setActionPlanShareLinkExpiry}
                        shareRecipientEmails={actionPlanShareRecipientEmails}
                        onShareRecipientEmailsChange={setActionPlanShareRecipientEmails}
                        shareLinkNotice={actionPlanShareLinkNotice}
                        shareLinkError={actionPlanShareLinkError}
                        revokingShareLinkId={revokingActionPlanShareLinkId}
                        onGenerateShareLink={() => generateActionPlanShareLink(selectedDepartment)}
                        onRevokeShareLink={revokeActionPlanShareLink}
                        onCopyShareUrl={copyActionPlanShareUrl}
                        copiedLinkId={copiedActionPlanLinkId}
                    />
                )}

                {viewMode === 'department' && !selectedDepartment && (
                    <div className="rp-empty-state">
                        <div className="rp-empty-icon"><BarChart3 size={32} /></div>
                        <h3>Select a Department</h3>
                        <p>Choose a department to view anonymized sentiment analysis.</p>
                        <div className="rp-dept-cards">
                            {departments.map(dept => (
                                <button 
                                    key={dept} 
                                    className="rp-dept-card-btn"
                                    onClick={() => handleSelectDepartment(dept)}
                                >
                                    {dept}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {viewMode === 'department' && selectedDepartment && (
                    <DepartmentSentimentView 
                        department={selectedDepartment}
                        deptTrends={deptTrends}
                        sentimentReport={sentimentReport}
                        condensedSummary={condensedSummary}
                        loadingCondensed={loadingCondensed}
                        loadingSentiment={loadingSentiment}
                        showFullReport={showFullReport}
                        onShowFullReport={() => setShowFullReport(true)}
                        onGenerateReport={() => loadSentimentReport(selectedDepartment)}
                        showSharePanel={showSharePanel}
                        onToggleSharePanel={() => showSharePanel ? setShowSharePanel(false) : handleOpenSharePanel()}
                        shareLinks={shareLinks.filter(link => link.department === selectedDepartment && !link.is_revoked)}
                        loadingShareLinks={loadingShareLinks}
                        generatingShareLink={generatingShareLink}
                        shareLinkLabel={shareLinkLabel}
                        onShareLinkLabelChange={setShareLinkLabel}
                        shareLinkExpiry={shareLinkExpiry}
                        onShareLinkExpiryChange={setShareLinkExpiry}
                        shareRecipientEmails={shareRecipientEmails}
                        onShareRecipientEmailsChange={setShareRecipientEmails}
                        shareLinkNotice={shareLinkNotice}
                        shareLinkError={shareLinkError}
                        revokingShareLinkId={revokingShareLinkId}
                        onGenerateShareLink={() => generateShareLink(selectedDepartment)}
                        onRevokeShareLink={revokeShareLink}
                        onCopyShareUrl={copyShareUrl}
                        copiedLinkId={copiedLinkId}
                        showDepartmentSummarySharePanel={showDepartmentSummarySharePanel}
                        onToggleDepartmentSummarySharePanel={() => showDepartmentSummarySharePanel ? setShowDepartmentSummarySharePanel(false) : handleOpenDepartmentSummarySharePanel()}
                        departmentSummaryShareLinks={departmentSummaryShareLinks.filter(link => link.department === selectedDepartment && !link.is_revoked)}
                        loadingDepartmentSummaryShareLinks={loadingDepartmentSummaryShareLinks}
                        generatingDepartmentSummaryShareLink={generatingDepartmentSummaryShareLink}
                        departmentSummaryShareLinkLabel={departmentSummaryShareLinkLabel}
                        onDepartmentSummaryShareLinkLabelChange={setDepartmentSummaryShareLinkLabel}
                        departmentSummaryShareLinkExpiry={departmentSummaryShareLinkExpiry}
                        onDepartmentSummaryShareLinkExpiryChange={setDepartmentSummaryShareLinkExpiry}
                        departmentSummaryShareRecipientEmails={departmentSummaryShareRecipientEmails}
                        onDepartmentSummaryShareRecipientEmailsChange={setDepartmentSummaryShareRecipientEmails}
                        departmentSummaryShareLinkNotice={departmentSummaryShareLinkNotice}
                        departmentSummaryShareLinkError={departmentSummaryShareLinkError}
                        revokingDepartmentSummaryShareLinkId={revokingDepartmentSummaryShareLinkId}
                        onGenerateDepartmentSummaryShareLink={() => generateDepartmentSummaryShareLink(selectedDepartment)}
                        onRevokeDepartmentSummaryShareLink={revokeDepartmentSummaryShareLink}
                        onCopyDepartmentSummaryShareUrl={copyDepartmentSummaryShareUrl}
                        copiedDepartmentSummaryLinkId={copiedDepartmentSummaryLinkId}
                        departmentParticipants={departmentParticipants}
                        loadingDepartmentParticipants={loadingDepartmentParticipants}
                        departmentParticipantsError={departmentParticipantsError}
                        departmentParticipantsLoaded={departmentParticipantsLoaded}
                        selectedParticipantEmails={selectedParticipantEmails}
                        onFetchDepartmentParticipants={() => fetchDepartmentParticipants(selectedDepartment)}
                        onToggleParticipantSelection={toggleParticipantSelection}
                        onSetAllParticipantsSelected={setAllParticipantsSelected}
                        onPreviewDepartmentSummary={() => openDepartmentSummaryPreview(selectedDepartment)}
                        showDepartmentSummaryPreview={showDepartmentSummaryPreview}
                        departmentSummaryPreviewData={departmentSummaryPreviewData}
                        loadingDepartmentSummaryPreview={loadingDepartmentSummaryPreview}
                        departmentSummaryPreviewError={departmentSummaryPreviewError}
                        onCloseDepartmentSummaryPreview={closeDepartmentSummaryPreview}
                        generationLocked={generationLocked}
                        generationLockMessage={generationLockMessage}
                    />
                )}
                {viewMode === 'actionable' && selectedDepartment && (
                    <ActionableReportView 
                        department={selectedDepartment}
                        actionableReport={actionableReport}
                        loadingActionable={loadingActionable}
                        sentimentReport={sentimentReport}
                        onGenerateReport={() => loadSentimentReport(selectedDepartment)}
                        onBackToDepartmentSelect={() => {
                            setSelectedDepartment(null);
                            setActionableReport(null);
                        }}
                        onRegenerateActionable={() => {
                            if (generationLocked) {
                                setActionableReport({ error: generationLockMessage });
                                return;
                            }
                            setActionableReport(null);
                            generateActionableReport(selectedDepartment, true);
                        }}
                        generationLocked={generationLocked}
                        generationLockMessage={generationLockMessage}
                        showSharePanel={showActionPlanSharePanel}
                        onToggleSharePanel={() => showActionPlanSharePanel ? setShowActionPlanSharePanel(false) : handleOpenActionPlanSharePanel()}
                        shareLinks={actionPlanShareLinks.filter(link => link.department === selectedDepartment && !link.is_revoked)}
                        loadingShareLinks={loadingActionPlanShareLinks}
                        generatingShareLink={generatingActionPlanShareLink}
                        shareLinkLabel={actionPlanShareLinkLabel}
                        onShareLinkLabelChange={setActionPlanShareLinkLabel}
                        shareLinkExpiry={actionPlanShareLinkExpiry}
                        onShareLinkExpiryChange={setActionPlanShareLinkExpiry}
                        shareRecipientEmails={actionPlanShareRecipientEmails}
                        onShareRecipientEmailsChange={setActionPlanShareRecipientEmails}
                        shareLinkNotice={actionPlanShareLinkNotice}
                        shareLinkError={actionPlanShareLinkError}
                        revokingShareLinkId={revokingActionPlanShareLinkId}
                        onGenerateShareLink={() => generateActionPlanShareLink(selectedDepartment)}
                        onRevokeShareLink={revokeActionPlanShareLink}
                        onCopyShareUrl={copyActionPlanShareUrl}
                        copiedLinkId={copiedActionPlanLinkId}
                    />
                )}

                {viewMode === 'actionable' && !selectedDepartment && (
                    <div className="rp-empty-state actionable-empty">
                        <div className="rp-empty-icon"><Target size={32} /></div>
                        <h3>Action Items Report</h3>
                        <p>AI-powered analysis of survey data to identify immediate actions, quick wins, and strategic focus areas.</p>
                        <p className="rp-empty-subtitle">Select a department to generate actionable insights:</p>
                        <div className="rp-dept-cards">
                            {departments.map(dept => (
                                <button 
                                    key={dept} 
                                    className="rp-dept-card-btn actionable-dept-btn"
                                    onClick={() => handleSelectDepartmentForActionable(dept)}
                                >
                                    <span className="dept-btn-icon"><ClipboardList size={16} /></span>
                                    <span className="dept-btn-name">{dept}</span>
                                    <span className="dept-btn-action">View Action Items →</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* NEW: Retention Risk View */}
                {viewMode === 'retention' && (
                    <RetentionRiskView
                        sentimentReport={selectedDepartment ? sentimentReport : null}
                        orgReport={orgReport}
                        isOrgLevel={!selectedDepartment}
                        loading={selectedDepartment ? loadingSentiment : loadingOrgReport}
                        onRegenerate={selectedDepartment ? handleRegenerateSentiment : () => loadOrgReport(true)}
                    />
                )}

                {/* NEW: Automation & Outsource View */}
                {viewMode === 'automation' && (
                    <AutomationOutsourceView
                        sentimentReport={selectedDepartment ? sentimentReport : null}
                        orgReport={orgReport}
                        isOrgLevel={!selectedDepartment}
                        loading={selectedDepartment ? loadingSentiment : loadingOrgReport}
                        onRegenerate={selectedDepartment ? handleRegenerateSentiment : () => loadOrgReport(true)}
                    />
                )}

                {/* NEW: Strategic Timeline View */}
                {viewMode === 'timeline' && (
                    <StrategicTimelineView
                        sentimentReport={selectedDepartment ? sentimentReport : null}
                        orgReport={orgReport}
                        isOrgLevel={!selectedDepartment}
                        loading={selectedDepartment ? loadingSentiment : loadingOrgReport}
                        onRegenerate={selectedDepartment ? handleRegenerateSentiment : () => loadOrgReport(true)}
                    />
                )}

                {/* NEW: Org Design View */}
                {viewMode === 'orgdesign' && (
                    <OrgDesignView
                        orgReport={orgReport}
                        loading={loadingOrgReport}
                        onRegenerate={() => loadOrgReport(true)}
                    />
                )}

                {/* NEW: Pulse Feedback View */}
                {viewMode === 'pulse' && (
                    <PulseFeedbackView
                        department={selectedDepartment}
                    />
                )}

                {/* NEW: Report Builder View */}
                {viewMode === 'builder' && (
                    <ReportBuilder
                        departments={departments}
                        orgReport={orgReport}
                        loadOrgReport={loadOrgReport}
                    />
                )}

                {viewMode === 'department' && selectedDepartment && loading && !deptTrends && (
                    <div className="rp-loading">
                        <div className="rp-spinner"></div>
                        <p>Loading {selectedDepartment} data...</p>
                    </div>
                )}
            </main>

            {/* Print Footer */}
            <footer className="rp-print-footer print-only">
                <p>AI Work Audit - Leadership Sentiment Report • Confidential • {new Date().getFullYear()}</p>
            </footer>
        </div>
    );
}

/**
 * Organization Overview (High Level Stats Only)
 */
function OrganizationOverview({ trends, onSelectDepartment }) {
    if (!trends) return null;

    const departments = trends.department_summary || [];
    const totalResponses = trends.total_reports || departments.reduce((sum, d) => sum + (d.completed_surveys || 0), 0) || 0;
    const totalDepartments = trends.departments_analyzed || departments.length || 0;

    return (
        <div className="rp-org-view">
            <ReportSection title="Organization Snapshot" icon={<ClipboardList size={16} />}>
                <div className="leadership-overview-grid">
                    <div className="leadership-stat-card large">
                        <span className="leadership-stat-icon"><Pencil size={16} /></span>
                        <span className="leadership-stat-value">{totalResponses}</span>
                        <span className="leadership-stat-label">Total Survey Responses</span>
                    </div>
                    <div className="leadership-stat-card large">
                        <span className="leadership-stat-icon"><Building2 size={16} /></span>
                        <span className="leadership-stat-value">{totalDepartments}</span>
                        <span className="leadership-stat-label">Departments Participating</span>
                    </div>
                </div>
                
                <p className="leadership-intro-text">
                    Select a department below to view detailed, anonymized sentiment analysis including 
                    team morale indicators, workload health, and strategic recommendations.
                </p>
            </ReportSection>

            <ReportSection title="Department Participation" icon={<Building2 size={16} />}>
                <div className="leadership-dept-grid">
                    {departments.map((dept, idx) => (
                        <div 
                            key={idx} 
                            className="leadership-dept-card"
                            onClick={() => onSelectDepartment(dept.department)}
                        >
                            <h3>{dept.department}</h3>
                            <div className="leadership-dept-stat">
                                <span className="value">{dept.completed_surveys || 0}</span>
                                <span className="label">Responses</span>
                            </div>
                            <button className="leadership-view-btn">View Sentiment Report →</button>
                        </div>
                    ))}
                </div>
            </ReportSection>
        </div>
    );
}

/**
 * Organization-Wide Report View
 * Shows cross-department comparisons, systemic trends, and org health
 */
function OrganizationReportView({ orgReport, loadingOrgReport, orgProgress, departments, onGenerate, onRegenerate, generationLocked, generationLockMessage }) {
    // Loading state
    if (loadingOrgReport) {
        return (
            <div className="leadership-loading org-loading">
                <div className="rp-spinner-large"></div>
                <h3>Generating Organization Report</h3>
                {orgProgress ? (
                    <p className="loading-detail">{orgProgress}</p>
                ) : (
                    <p>AI is reading all department surveys and comparing trends across the entire company...</p>
                )}
                <p className="loading-detail">Refreshing all department data then synthesizing org-wide analysis.</p>
                <p className="loading-time-estimate"><Clock size={14} /> This may take several minutes for a full refresh</p>
            </div>
        );
    }

    // No report — prompt to generate
    if (!orgReport) {
        return (
            <div className="rp-dept-view">
                <ReportSection title="Organization-Wide Report" icon={<Globe size={16} />}>
                    <div className="leadership-generate-prompt org-generate">
                        <div className="leadership-prompt-icon"><Globe size={32} /></div>
                        <h3>Generate Cross-Department Comparison</h3>
                        <p>
                            This report reads <strong>all department surveys</strong> and uses AI to compare
                            departments against each other, surface systemic patterns, and identify
                            organization-wide strategic priorities.
                        </p>
                        <div className="leadership-features">
                            <div className="leadership-feature"><Check size={14} /> Department-by-department comparison heatmap</div>
                            <div className="leadership-feature"><Check size={14} /> Systemic patterns across the organization</div>
                            <div className="leadership-feature"><Check size={14} /> Shared pain points & infrastructure gaps</div>
                            <div className="leadership-feature"><Check size={14} /> Strategic priorities ranked by org-wide impact</div>
                            <div className="leadership-feature"><Check size={14} /> Fully anonymized — no employee names</div>
                        </div>
                        <p className="generate-note">
                            Requires at least one department sentiment report to have been generated.
                            {departments.length > 0 && ` ${departments.length} departments available.`}
                        </p>
                        {generationLocked && <p className="generate-note"><Lock size={14} /> {generationLockMessage}</p>}
                        <button className="rp-btn rp-btn-primary leadership-generate-btn" onClick={onGenerate} disabled={generationLocked}>
                            <Globe size={14} /> Generate Organization Report
                        </button>
                    </div>
                </ReportSection>
            </div>
        );
    }

    // Error state
    if (orgReport?.error) {
        return (
            <div className="rp-dept-view">
                <ReportSection title="Organization Report" icon={<Globe size={16} />}>
                    <div className="leadership-error">
                        <span className="leadership-error-icon"><AlertTriangle size={16} /></span>
                        <h3>Unable to Generate Report</h3>
                        <p>{orgReport.error}</p>
                        <button className="rp-btn rp-btn-primary" onClick={onGenerate} disabled={generationLocked}>
                            <RefreshCw size={14} /> Try Again
                        </button>
                    </div>
                </ReportSection>
            </div>
        );
    }

    const analysis = orgReport?.analysis;
    if (!analysis) return null;

    const deptsIncluded = orgReport?.departments_included || [];
    const totalRespondents = orgReport?.total_respondents || 0;
    const deptsCount = orgReport?.departments_count || deptsIncluded.length;

    return (
        <div className="rp-dept-view org-report-view">
            {/* Report Header */}
            <div className="org-report-header">
                <div className="org-report-title">
                    <h2><Globe size={16} /> Organization-Wide Report</h2>
                    <p className="org-report-subtitle">
                        {deptsCount} departments • {totalRespondents} total respondents • 
                        {deptsIncluded.join(', ')}
                    </p>
                </div>
                <div className="org-report-actions">
                    <button className="rp-btn rp-btn-primary" onClick={onRegenerate} disabled={generationLocked} title="Regenerate with latest data">
                        <RefreshCw size={14} /> Regenerate
                    </button>
                    {orgReport?.generated_at && (
                        <span className="org-report-date">
                            Generated {new Date(orgReport.generated_at).toLocaleDateString()}
                        </span>
                    )}
                </div>
            </div>

            {/* Executive TLDR - The front page for leadership */}
            <ExecutiveTLDR 
                orgReport={orgReport} 
                onViewFullReport={() => {
                    const el = document.getElementById('org-full-report-start');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
            />

            {/* Full Report Divider */}
            <div className="org-full-report-divider" id="org-full-report-start">
                <div className="org-full-report-divider-line" />
                <span className="org-full-report-divider-text"><FileText size={16} /> Full Detailed Report</span>
                <div className="org-full-report-divider-line" />
            </div>

            {/* Executive Summary */}
            {analysis.executive_summary && (
                <div id="org-section-executive-summary" className="org-executive-summary">
                    <p>{analysis.executive_summary}</p>
                </div>
            )}

            {/* Organization Health Dashboard */}
            {analysis.organization_health_summary && (
                <ReportSection title="Organization Health" icon={<Heart size={16} style={{ color: 'var(--color-success)' }} />} id="org-section-health">
                    <div className="org-health-dashboard">
                        <div className="org-health-card main">
                            <span className="org-health-label">Overall Health</span>
                            <SentimentBadge value={analysis.organization_health_summary.overall_health_rating} type="sentiment" />
                        </div>
                        <div className="org-health-card">
                            <span className="org-health-label">Trajectory</span>
                            <SentimentBadge value={analysis.organization_health_summary.trajectory} type="sentiment" />
                        </div>
                        <div className="org-health-card">
                            <span className="org-health-label">Strongest Area</span>
                            <span className="org-health-value">{analysis.organization_health_summary.strongest_area}</span>
                        </div>
                        <div className="org-health-card">
                            <span className="org-health-label">Needs Attention</span>
                            <span className="org-health-value concern">{analysis.organization_health_summary.weakest_area}</span>
                        </div>
                    </div>
                    {analysis.organization_health_summary.overall_assessment && (
                        <p className="org-health-assessment">{analysis.organization_health_summary.overall_assessment}</p>
                    )}
                    {analysis.organization_health_summary.trajectory_explanation && (
                        <p className="org-health-trajectory">
                            <strong>Trajectory:</strong> {analysis.organization_health_summary.trajectory_explanation}
                        </p>
                    )}
                </ReportSection>
            )}

            {/* Department Comparison Heatmap */}
            {analysis.department_comparison?.comparison_matrix && (
                <ReportSection title="Department Comparison" icon={<BarChart3 size={16} />} id="org-section-comparison">
                    <div className="org-heatmap-container">
                        <table className="org-heatmap-table">
                            <thead>
                                <tr>
                                    <th>Metric</th>
                                    {deptsIncluded.map(d => <th key={d}>{d}</th>)}
                                    <th>Org Pattern</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.department_comparison.comparison_matrix.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="metric-name">
                                            <strong>{row.metric}</strong>
                                            {row.description && <span className="metric-desc">{row.description}</span>}
                                        </td>
                                        {deptsIncluded.map(d => (
                                            <td key={d} className={getHeatmapClass(row[d])}>
                                                {row[d] || '—'}
                                            </td>
                                        ))}
                                        <td className="org-pattern-cell">{row.org_pattern}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Healthiest vs Most At-Risk */}
                    <div className="org-comparison-highlights">
                        {analysis.department_comparison.healthiest_department && (
                            <div className="org-highlight-card positive">
                                <div className="org-highlight-header">
                                    <span className="org-highlight-icon"><Star size={16} /></span>
                                    <h4>Healthiest: {analysis.department_comparison.healthiest_department.department}</h4>
                                </div>
                                <p>{analysis.department_comparison.healthiest_department.strengths}</p>
                                {analysis.department_comparison.healthiest_department.lessons_for_others && (
                                    <p className="org-highlight-lesson">
                                        <Lightbulb size={14} /> {analysis.department_comparison.healthiest_department.lessons_for_others}
                                    </p>
                                )}
                            </div>
                        )}
                        {analysis.department_comparison.most_at_risk_department && (
                            <div className="org-highlight-card concern">
                                <div className="org-highlight-header">
                                    <span className="org-highlight-icon"><AlertTriangle size={16} /></span>
                                    <h4>Most At Risk: {analysis.department_comparison.most_at_risk_department.department}</h4>
                                </div>
                                <p>{analysis.department_comparison.most_at_risk_department.concerns}</p>
                                {analysis.department_comparison.most_at_risk_department.recommended_intervention && (
                                    <p className="org-highlight-action">
                                        <Target size={14} /> {analysis.department_comparison.most_at_risk_department.recommended_intervention}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </ReportSection>
            )}

            {/* Systemic Patterns */}
            {analysis.systemic_patterns && analysis.systemic_patterns.length > 0 && (
                <ReportSection title="Systemic Patterns" icon={<Search size={16} />} id="org-section-patterns">
                    <p className="section-description">Patterns detected across multiple departments — these are organizational issues, not department-specific</p>
                    <div className="org-patterns-list">
                        {analysis.systemic_patterns.map((pattern, idx) => (
                            <div key={idx} className={`org-pattern-card severity-${pattern.severity}`}>
                                <div className="org-pattern-header">
                                    <span className={`org-pattern-type ${pattern.pattern_type}`}>
                                        {pattern.pattern_type}
                                    </span>
                                    <SentimentBadge value={pattern.severity} type="risk" />
                                </div>
                                <h4>{pattern.pattern}</h4>
                                <p className="org-pattern-desc">{pattern.description}</p>
                                {pattern.evidence && (
                                    <p className="org-pattern-evidence"><strong>Evidence:</strong> {pattern.evidence}</p>
                                )}
                                {pattern.business_impact && (
                                    <p className="org-pattern-impact"><Briefcase size={14} /> <strong>Business Impact:</strong> {pattern.business_impact}</p>
                                )}
                                <div className="org-pattern-depts">
                                    {(pattern.departments_affected || []).map((d, i) => (
                                        <span key={i} className="org-dept-pill">{d}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Shared Pain Points */}
            {analysis.shared_pain_points && analysis.shared_pain_points.length > 0 && (
                <ReportSection title="Shared Pain Points" icon={<Link size={16} />} id="org-section-pain-points">
                    <p className="section-description">Issues independently reported across multiple departments</p>
                    <div className="org-pain-points-list">
                        {analysis.shared_pain_points.map((pp, idx) => (
                            <div key={idx} className="org-pain-point-card">
                                <h4>{pp.pain_point}</h4>
                                <p>{pp.description}</p>
                                {pp.estimated_org_impact && (
                                    <p className="org-pp-impact"><BarChart3 size={14} /> <strong>Org Impact:</strong> {pp.estimated_org_impact}</p>
                                )}
                                {pp.root_cause_analysis && (
                                    <p className="org-pp-root"><Search size={14} /> <strong>Root Cause:</strong> {pp.root_cause_analysis}</p>
                                )}
                                <div className="org-pattern-depts">
                                    {(pp.departments_reporting || []).map((d, i) => (
                                        <span key={i} className="org-dept-pill">{d}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Strategic Priorities */}
            {analysis.strategic_priorities && analysis.strategic_priorities.length > 0 && (
                <ReportSection title="Strategic Priorities" icon={<Target size={16} />} id="org-section-priorities">
                    <p className="section-description">Ranked by organization-wide impact across all departments</p>
                    <div className="org-priorities-list">
                        {analysis.strategic_priorities.map((priority, idx) => (
                            <div key={idx} className="org-priority-card">
                                <div className="org-priority-rank">#{priority.rank || idx + 1}</div>
                                <div className="org-priority-content">
                                    <h4>{priority.priority}</h4>
                                    <p>{priority.description}</p>
                                    <div className="org-priority-meta">
                                        {priority.timeline && (
                                            <span className="org-priority-timeline">
                                                <Clock size={14} /> {priority.timeline}
                                            </span>
                                        )}
                                        {priority.investment_level && (
                                            <span className="org-priority-invest">
                                                <DollarSign size={14} /> {priority.investment_level}
                                            </span>
                                        )}
                                        {priority.estimated_roi && (
                                            <span className="org-priority-roi">
                                                <TrendingUp size={14} /> {priority.estimated_roi}
                                            </span>
                                        )}
                                    </div>
                                    {priority.departments_impacted && priority.departments_impacted.length > 0 && (
                                        <div className="org-pattern-depts">
                                            {priority.departments_impacted.map((d, i) => (
                                                <span key={i} className="org-dept-pill">{d}</span>
                                            ))}
                                        </div>
                                    )}
                                    {priority.success_metrics && (
                                        <p className="org-priority-metrics"><Ruler size={14} /> <strong>Success Metrics:</strong> {priority.success_metrics}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Cross-Department Risks */}
            {analysis.cross_department_risks && analysis.cross_department_risks.length > 0 && (
                <ReportSection title="Cross-Department Risks" icon={<AlertTriangle size={16} />} id="org-section-risks">
                    <div className="org-risks-list">
                        {analysis.cross_department_risks.map((risk, idx) => (
                            <div key={idx} className={`org-risk-card severity-${risk.severity}`}>
                                <div className="org-risk-header">
                                    <h4>{risk.risk}</h4>
                                    <SentimentBadge value={risk.severity} type="risk" />
                                </div>
                                <p>{risk.description}</p>
                                {risk.if_unaddressed && (
                                    <p className="org-risk-unaddressed"><Zap size={14} /> <strong>If unaddressed:</strong> {risk.if_unaddressed}</p>
                                )}
                                {risk.recommended_action && (
                                    <p className="org-risk-action"><Target size={14} /> <strong>Action:</strong> {risk.recommended_action}</p>
                                )}
                                <div className="org-pattern-depts">
                                    {(risk.affected_departments || []).map((d, i) => (
                                        <span key={i} className="org-dept-pill">{d}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Unique Department Strengths */}
            {analysis.unique_department_strengths && analysis.unique_department_strengths.length > 0 && (
                <ReportSection title="Unique Department Strengths" icon={<Star size={16} />} id="org-section-strengths">
                    <p className="section-description">What each department does well that others can learn from</p>
                    <div className="org-strengths-grid">
                        {analysis.unique_department_strengths.map((s, idx) => (
                            <div key={idx} className="org-strength-card">
                                <h4>{s.department}</h4>
                                <p className="org-strength-desc">{s.strength}</p>
                                {s.transferability && (
                                    <p className="org-strength-transfer"><RefreshCw size={14} /> {s.transferability}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* 90-Day Outlook */}
            {analysis['90_day_organization_outlook'] && (
                <ReportSection title="90-Day Organization Outlook" icon={<Calendar size={16} />} id="org-section-outlook">
                    <div className="org-outlook">
                        <div className="org-outlook-trajectory">
                            <span className="outlook-label">Expected Trajectory:</span>
                            <SentimentBadge value={analysis['90_day_organization_outlook'].expected_trajectory} type="sentiment" />
                        </div>
                        {analysis['90_day_organization_outlook'].success_indicators && (
                            <p className="org-outlook-success">
                                <CheckCircle2 size={14} /> <strong>Success looks like:</strong> {analysis['90_day_organization_outlook'].success_indicators}
                            </p>
                        )}
                        {analysis['90_day_organization_outlook'].key_org_factors && (
                            <div className="org-outlook-factors">
                                <h4>Key Factors to Watch</h4>
                                <ul>
                                    {analysis['90_day_organization_outlook'].key_org_factors.map((f, i) => (
                                        <li key={i}>{f}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {analysis['90_day_organization_outlook'].department_specific_watchpoints && (
                            <div className="org-outlook-watchpoints">
                                <h4>Department Watchpoints</h4>
                                {analysis['90_day_organization_outlook'].department_specific_watchpoints.map((wp, i) => (
                                    <div key={i} className="org-watchpoint">
                                        <strong>{wp.department}:</strong> {wp.watchpoint}
                                        {wp.why && <span className="watchpoint-why"> — {wp.why}</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </ReportSection>
            )}

            {/* Leadership Discussion Questions */}
            {analysis.leadership_discussion_questions && analysis.leadership_discussion_questions.length > 0 && (
                <ReportSection title="Discussion Questions for Leadership" icon={<MessageSquare size={16} />} id="org-section-discussion">
                    <div className="org-discussion-list">
                        {analysis.leadership_discussion_questions.map((q, idx) => (
                            <div key={idx} className="org-discussion-item">
                                <span className="discussion-number">{idx + 1}</span>
                                <p>{q}</p>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Footer */}
            <div className="leadership-report-footer">
                <p className="leadership-privacy-note">
                    <Lock size={14} /> This report contains no employee names or identifiable information. All data is anonymized and aggregated at the department level.
                </p>
            </div>
        </div>
    );
}

/**
 * Helper to determine heatmap cell color class based on the value text
 */
function getHeatmapClass(value) {
    if (!value) return '';
    const v = value.toLowerCase();
    // Negative/concerning signals
    if (v.includes('high') || v.includes('critical') || v.includes('concerning') || 
        v.includes('elevated') || v.includes('strained') || v.includes('poor') ||
        v.includes('low') && (v.includes('satisfaction') || v.includes('efficiency'))) {
        return 'heat-red';
    }
    // Positive signals  
    if (v.includes('strong') || v.includes('positive') || v.includes('good') || 
        v.includes('adequate') || v.includes('healthy')) {
        return 'heat-green';
    }
    // Neutral/moderate
    if (v.includes('moderate') || v.includes('mixed') || v.includes('medium') || v.includes('fair')) {
        return 'heat-yellow';
    }
    return '';
}

/**
 * Condensed Summary Card Component
 * Displays a quick overview with top 3-5 highlights
 */
const CondensedSummaryCard = ({ summary, onViewFullReport }) => {
    if (!summary || !summary.highlights || summary.highlights.length === 0) {
        return null;
    }

    return (
        <div className="condensed-summary-section">
            <div className="condensed-header">
                <div className="condensed-title">
                    <span className="condensed-icon"><Zap size={16} /></span>
                    <h3>Quick Summary</h3>
                </div>
                <button className="condensed-expand-btn" onClick={onViewFullReport}>
                    View Full Report →
                </button>
            </div>
            
            <p className="condensed-text">{summary.summary_text}</p>
            
            {/* Quick Stats Row */}
            {summary.quick_stats && (
                <div className="condensed-stats-row">
                    {summary.quick_stats.overall_sentiment && summary.quick_stats.overall_sentiment !== 'Unknown' && (
                        <div className="condensed-stat">
                            <span className="stat-label">Sentiment</span>
                            <SentimentBadge value={summary.quick_stats.overall_sentiment} type="sentiment" />
                        </div>
                    )}
                    {summary.quick_stats.burnout_risk && summary.quick_stats.burnout_risk !== 'Unknown' && (
                        <div className="condensed-stat">
                            <span className="stat-label">Burnout Risk</span>
                            <SentimentBadge value={summary.quick_stats.burnout_risk} type="risk" />
                        </div>
                    )}
                    {summary.quick_stats.risks_count > 0 && (
                        <div className="condensed-stat">
                            <span className="stat-label">Active Risks</span>
                            <span className="stat-value risk">{summary.quick_stats.risks_count}</span>
                        </div>
                    )}
                    {summary.quick_stats.recommendations_count > 0 && (
                        <div className="condensed-stat">
                            <span className="stat-label">Recommendations</span>
                            <span className="stat-value">{summary.quick_stats.recommendations_count}</span>
                        </div>
                    )}
                </div>
            )}
            
            {/* Key Themes */}
            {summary.themes && summary.themes.length > 0 && (
                <div className="condensed-themes">
                    <span className="themes-label">Key Themes:</span>
                    <div className="themes-tags">
                        {summary.themes.map((theme, idx) => (
                            <span key={idx} className="theme-tag">{theme}</span>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Top Highlights */}
            <div className="condensed-highlights">
                <h4>Top Highlights</h4>
                <div className="highlights-grid">
                    {summary.highlights.map((highlight, idx) => (
                        <div key={idx} className={`highlight-card ${highlight.status_type || 'default'}`}>
                            <div className="highlight-header">
                                <span className="highlight-category">{highlight.category}</span>
                                <SentimentBadge value={highlight.status} type={highlight.status_type || 'sentiment'} />
                            </div>
                            <p className="highlight-detail">{highlight.detail}</p>
                            {highlight.business_impact && (
                                <p className="highlight-impact"><Briefcase size={14} /> {highlight.business_impact}</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/**
 * Actionable Report View - Shows specific action items for leadership
 * Provides immediate actions, quick wins, and focus areas
 * Now operates independently - can generate/regenerate without sentiment report
 */
// Chronological roadmap horizons for the Timeline section. Items bucket into the
// first matching column; unrecognized timelines fall into "Later / Unscheduled".
const ACTION_TIMELINE_COLUMNS = [
    { key: 'this-week', label: 'This Week', match: ['this week'] },
    { key: 'two-weeks', label: 'Within 2 Weeks', match: ['within 2 weeks', '2 weeks', 'two weeks'] },
    { key: '0-6', label: '0\u20136 Months', match: ['0-6', '0\u20136', '0 - 6'] },
    { key: '6-12', label: '6\u201312 Months', match: ['6-12', '6\u201312', '6 - 12'] },
    { key: '1-2y', label: '1\u20132 Years', match: ['1-2 year', '1\u20132 year', '1 - 2 year'] },
    { key: '3-5y', label: '3\u20135 Years', match: ['3-5 year', '3\u20135 year', '3 - 5 year'] },
];

function bucketActionTimeline(timeline) {
    const text = String(timeline || '').toLowerCase();
    for (const col of ACTION_TIMELINE_COLUMNS) {
        if (col.match.some((m) => text.includes(m))) return col.key;
    }
    return 'later';
}

const MATRIX_CELLS = [
    { key: 'quick-wins', tone: 'qi-quick-wins', label: 'Quick Wins', axis: 'High Impact \u00b7 Low Effort', match: 'quick win' },
    { key: 'major-projects', tone: 'qi-major-projects', label: 'Major Projects', axis: 'High Impact \u00b7 High Effort', match: 'major' },
    { key: 'fill-ins', tone: 'qi-fill-ins', label: 'Fill-Ins', axis: 'Low Impact \u00b7 Low Effort', match: 'fill' },
    { key: 'reconsider', tone: 'qi-reconsider', label: 'Reconsider', axis: 'Low Impact \u00b7 High Effort', match: 'reconsider' },
];

// Gus paraphrases titles between the matrix and the action lists, so we match on token
// overlap rather than exact strings.
const TL_TITLE_STOP_WORDS = new Set(['the', 'a', 'an', 'and', 'or', 'to', 'of', 'for', 'in', 'on', 'with', 'not', 'by', 'at', 'is', 'this']);

function tlTitleTokenSet(s) {
    return new Set(
        String(s || '')
            .toLowerCase()
            .split(/[^a-z0-9]+/)
            .filter((t) => t.length > 1 && !TL_TITLE_STOP_WORDS.has(t))
    );
}

// Build a token index of matrix items -> quadrant key so timeline cards inherit the matrix color + tag.
function buildMatrixQuadrantIndex(matrix) {
    const index = [];
    (Array.isArray(matrix) ? matrix : []).forEach((q) => {
        const cell = MATRIX_CELLS.find((c) => String(q.quadrant || '').toLowerCase().includes(c.match));
        if (!cell) return;
        (Array.isArray(q.items) ? q.items : []).forEach((it) => {
            const tokens = tlTitleTokenSet(it.title);
            if (tokens.size) index.push({ key: cell.key, tokens });
        });
    });
    return index;
}

// Resolve an item to a matrix quadrant: best fuzzy title match first, then effort/impact, else null.
function resolveQuadrantKey(item, index) {
    const itemTokens = tlTitleTokenSet(item.title);
    if (itemTokens.size) {
        let best = null;
        let bestScore = 0;
        for (const entry of index) {
            let hit = 0;
            for (const t of itemTokens) if (entry.tokens.has(t)) hit++;
            const ratio = hit / Math.min(itemTokens.size, entry.tokens.size);
            if (hit >= 2 && ratio >= 0.6 && ratio > bestScore) {
                best = entry.key;
                bestScore = ratio;
            }
        }
        if (best) return best;
    }
    const effort = String(item.effort || '');
    const impact = String(item.impact || '');
    if (effort && impact) {
        const highImpact = /high/i.test(impact);
        const lowEffort = /low/i.test(effort);
        if (highImpact && lowEffort) return 'quick-wins';
        if (highImpact) return 'major-projects';
        if (lowEffort) return 'fill-ins';
        return 'reconsider';
    }
    return null;
}

function ActionableReportView({ 
    department, 
    actionableReport, 
    loadingActionable, 
    sentimentReport,
    onGenerateReport,
    onBackToDepartmentSelect,
    onRegenerateActionable,
    generationLocked,
    generationLockMessage,
    showSharePanel,
    onToggleSharePanel,
    shareLinks,
    loadingShareLinks,
    generatingShareLink,
    shareLinkLabel,
    onShareLinkLabelChange,
    shareLinkExpiry,
    onShareLinkExpiryChange,
    shareRecipientEmails,
    onShareRecipientEmailsChange,
    shareLinkNotice,
    shareLinkError,
    revokingShareLinkId,
    onGenerateShareLink,
    onRevokeShareLink,
    onCopyShareUrl,
    copiedLinkId
}) {
    // Loading state
    if (loadingActionable) {
        return (
            <div className="leadership-loading actionable-loading">
                <div className="rp-spinner-large"></div>
                <h3>Building Department Action Roadmap</h3>
                <p>AI is analyzing all survey responses against the company 5-year plan to identify actionable insights...</p>
                <p className="loading-detail">This examines workflow challenges, tool issues, improvement suggestions, cross-functional impacts, and strategic alignment.</p>
                <p className="loading-time-estimate"><Clock size={14} /> This typically takes 1-2 minutes</p>
            </div>
        );
    }

    // No actionable report yet - show generate prompt
    if (!actionableReport || actionableReport.needsSentimentReport) {
        return (
            <div className="rp-dept-view">
                <ReportSection title={`${department} Action Roadmap`} icon={<Target size={16} />}>
                    <div className="leadership-generate-prompt actionable-generate">
                        <div className="leadership-prompt-icon"><ClipboardList size={32} /></div>
                        <h3>Generate Action Roadmap</h3>
                        <p>
                            Analyze all survey responses from {department} against the company 5-year plan
                            to identify immediate actions, quick wins, and strategic focus areas for leadership.
                        </p>
                        <p className="generate-note">
                            This AI-powered analysis reviews workflow challenges, tool issues, improvement
                            suggestions, and workload signals, then steers them toward the strategic plan
                            with cross-functional impacts and alignment guidance.
                        </p>
                        {generationLocked && <p className="generate-note"><Lock size={14} /> {generationLockMessage}</p>}
                        <button 
                            className="rp-btn rp-btn-primary leadership-generate-btn actionable-generate-btn" 
                            onClick={onRegenerateActionable}
                            disabled={generationLocked}
                        >
                            <Target size={14} /> Generate Action Roadmap
                        </button>
                    </div>
                </ReportSection>
            </div>
        );
    }

    // Error state - show retry option
    if (actionableReport?.error) {
        return (
            <div className="rp-dept-view">
                <ReportSection title={`${department} Action Roadmap`} icon={<Target size={16} />}>
                    <div className="leadership-error">
                        <span className="leadership-error-icon"><AlertTriangle size={16} /></span>
                        <h3>Unable to Generate Action Roadmap</h3>
                        <p>{actionableReport.error}</p>
                        <div className="error-actions">
                            <button className="rp-btn rp-btn-primary" onClick={onRegenerateActionable} disabled={generationLocked}>
                                <RefreshCw size={14} /> Try Again
                            </button>
                            <button className="rp-btn rp-btn-secondary" onClick={onBackToDepartmentSelect}>
                                ← Choose Different Department
                            </button>
                        </div>
                    </div>
                </ReportSection>
            </div>
        );
    }

    const {
        immediate_actions,
        quick_wins,
        focus_areas,
        key_metrics,
        effort_impact_matrix,
        department_org_design,
        strategic_bets,
        summary
    } = actionableReport;
    const effortImpactMatrix = Array.isArray(effort_impact_matrix) ? effort_impact_matrix : [];
    const strategicBets = Array.isArray(strategic_bets) ? strategic_bets : [];
    const orgDesignRecommendations = Array.isArray(department_org_design?.recommendations) ? department_org_design.recommendations : [];
    const hasMatrixItems = effortImpactMatrix.some(quadrant => Array.isArray(quadrant.items) && quadrant.items.length > 0);
    const findMatrixQuadrant = (keyword) =>
        effortImpactMatrix.find(q => String(q.quadrant || '').toLowerCase().includes(keyword)) || null;
    const matrixQuadrantLookup = buildMatrixQuadrantIndex(effortImpactMatrix);

    // Build the Timeline (immediate + quick wins + focus areas bucketed by horizon).
    const timelineTagged = [
        ...(Array.isArray(immediate_actions) ? immediate_actions : []),
        ...(Array.isArray(quick_wins) ? quick_wins : []),
        ...(Array.isArray(focus_areas) ? focus_areas : []),
    ];
    const timelineColumns = [...ACTION_TIMELINE_COLUMNS, { key: 'later', label: 'Later / Unscheduled', match: [] }]
        .map(col => ({ ...col, entries: timelineTagged.filter(item => bucketActionTimeline(item.timeline) === col.key) }))
        .filter(col => col.entries.length > 0);

    // Check if report has any actual content
    const hasContent = (immediate_actions && immediate_actions.length > 0) || 
                       (quick_wins && quick_wins.length > 0) || 
                       (focus_areas && focus_areas.length > 0) ||
                       strategicBets.length > 0 ||
                       orgDesignRecommendations.length > 0;

    if (!hasContent && !key_metrics) {
        return (
            <div className="rp-dept-view">
                <ReportSection title={`${department} Action Roadmap`} icon={<Target size={16} />}>
                    <div className="leadership-generate-prompt">
                        <div className="leadership-prompt-icon"><BarChart3 size={32} /></div>
                        <h3>No Roadmap Items Found</h3>
                        <p>
                            The analysis didn't identify specific action items. This may indicate 
                            the department is performing well or needs more survey responses.
                        </p>
                        <div className="error-actions">
                            <button className="rp-btn rp-btn-primary" onClick={onRegenerateActionable} disabled={generationLocked}>
                                <RefreshCw size={14} /> Re-analyze Data
                            </button>
                            <button className="rp-btn rp-btn-secondary" onClick={onBackToDepartmentSelect}>
                                ← Choose Different Department
                            </button>
                        </div>
                    </div>
                </ReportSection>
            </div>
        );
    }

    return (
        <div className="rp-dept-view actionable-report">
            {/* Report Header */}
            <div className="actionable-header">
                <div className="actionable-title">
                    <h2><Target size={16} /> {department} Action Roadmap</h2>
                    <p className="actionable-subtitle">
                        {summary?.total_action_items || 0} actionable items identified • 
                        {actionableReport?.respondent_count && `Based on ${actionableReport.respondent_count} survey responses • `}
                        Confidential
                    </p>
                </div>
                <div className="actionable-header-actions">
                    <button
                        className={`rp-btn rp-btn-secondary share-btn ${showSharePanel ? 'active' : ''}`}
                        onClick={onToggleSharePanel}
                        title="Share this action plan"
                    >
                        <Share2 size={14} /> Share Action Roadmap
                    </button>
                    <button className="rp-btn rp-btn-primary" onClick={onRegenerateActionable} disabled={generationLocked} title="Re-analyze survey data for updated action items">
                        <RefreshCw size={14} /> Regenerate
                    </button>
                    <button className="rp-btn rp-btn-secondary" onClick={onBackToDepartmentSelect}>
                        ← Back to Overview
                    </button>
                </div>
            </div>

            {showSharePanel && (
                <div className="share-panel">
                    <div className="share-panel-header">
                        <h3><Link size={16} /> Action Roadmap Links for {department}</h3>
                        <button className="share-panel-close" onClick={onToggleSharePanel}>×</button>
                    </div>

                    <div className="share-generate-section">
                        <h4>Generate New Link</h4>
                        <div className="share-generate-form">
                            <div className="share-form-row">
                                <label className="share-field-group">
                                    <span>Label</span>
                                    <input
                                        type="text"
                                        className="share-input"
                                        placeholder="Optional, e.g. Department Head"
                                        value={shareLinkLabel}
                                        onChange={(event) => onShareLinkLabelChange(event.target.value)}
                                    />
                                </label>
                                <label className="share-field-group share-expiry-field">
                                    <span>Expiration</span>
                                    <select
                                        className="share-select"
                                        value={shareLinkExpiry}
                                        onChange={(event) => onShareLinkExpiryChange(Number(event.target.value))}
                                    >
                                        <option value={7}>7 days</option>
                                        <option value={14}>14 days</option>
                                        <option value={30}>30 days</option>
                                        <option value={60}>60 days</option>
                                        <option value={90}>90 days</option>
                                    </select>
                                </label>
                            </div>
                            <label className="share-field-group share-email-field">
                                <span><Mail size={13} /> Email recipients</span>
                                <textarea
                                    className="share-input share-email-input"
                                    placeholder="name@example.com, another@example.com"
                                    value={shareRecipientEmails}
                                    onChange={(event) => onShareRecipientEmailsChange(event.target.value)}
                                    rows={3}
                                />
                            </label>
                            {shareLinkError && (
                                <div className="share-status share-status-error">
                                    <AlertTriangle size={14} /> {shareLinkError}
                                </div>
                            )}
                            {shareLinkNotice && (
                                <div className="share-status share-status-success">
                                    <CheckCircle2 size={14} /> {shareLinkNotice}
                                </div>
                            )}
                            <button
                                className="rp-btn rp-btn-primary share-generate-btn"
                                onClick={onGenerateShareLink}
                                disabled={generatingShareLink}
                            >
                                {generatingShareLink ? <><Loader2 size={14} className="spin" /> Generating...</> : <><ExternalLink size={14} /> Generate Link</>}
                            </button>
                        </div>
                    </div>

                    <div className="share-links-section">
                        <h4>Existing Links</h4>
                        {loadingShareLinks ? (
                            <div className="share-links-loading"><Loader2 size={16} className="spin" /> Loading links...</div>
                        ) : shareLinks.length === 0 ? (
                            <p className="share-links-empty">No action plan links yet for this department.</p>
                        ) : (
                            <div className="share-links-list">
                                {shareLinks.map(link => (
                                    <div key={link.id} className={`share-link-item ${link.is_expired ? 'expired' : ''}`}>
                                        <div className="share-link-info">
                                            <div className="share-link-label">
                                                {link.label || 'Unnamed link'}
                                                {link.is_expired && <span className="share-link-expired-badge">Expired</span>}
                                            </div>
                                            <div className="share-link-meta">
                                                <span><Calendar size={12} /> {new Date(link.created_at).toLocaleDateString()}</span>
                                                {link.expires_at && <span><Clock size={12} /> Expires {new Date(link.expires_at).toLocaleDateString()}</span>}
                                                <span><Eye size={12} /> {link.access_count || 0} views</span>
                                                {link.feedback_submitted_at && (
                                                    <span className="share-link-feedback-badge">
                                                        <MessageSquare size={12} /> Feedback received
                                                    </span>
                                                )}
                                            </div>
                                            {link.feedback && (
                                                <div className="share-link-feedback">
                                                    <div className="share-link-feedback-head">
                                                        <strong><MessageSquare size={13} /> Roadmap feedback</strong>
                                                        <span>
                                                            {link.feedback.submitted_by ? `${link.feedback.submitted_by} • ` : ''}
                                                            {link.feedback_submitted_at ? new Date(link.feedback_submitted_at).toLocaleDateString() : ''}
                                                        </span>
                                                    </div>
                                                    {link.feedback.wants_meeting && (
                                                        <div className="share-link-feedback-meeting">
                                                            <Calendar size={12} /> Requested a meeting to discuss
                                                        </div>
                                                    )}
                                                    {link.feedback.reprioritization && (
                                                        <div className="share-link-feedback-item">
                                                            <span className="share-link-feedback-q">Re-ranking</span>
                                                            <p>{link.feedback.reprioritization}</p>
                                                        </div>
                                                    )}
                                                    {link.feedback.risks_dependencies && (
                                                        <div className="share-link-feedback-item">
                                                            <span className="share-link-feedback-q">Risks / dependencies / sequencing</span>
                                                            <p>{link.feedback.risks_dependencies}</p>
                                                        </div>
                                                    )}
                                                    {link.feedback.missing_or_overstated && (
                                                        <div className="share-link-feedback-item">
                                                            <span className="share-link-feedback-q">Missing / overstated</span>
                                                            <p>{link.feedback.missing_or_overstated}</p>
                                                        </div>
                                                    )}
                                                    {link.feedback.additional_notes && (
                                                        <div className="share-link-feedback-item">
                                                            <span className="share-link-feedback-q">Additional notes</span>
                                                            <p>{link.feedback.additional_notes}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="share-link-actions">
                                            {!link.is_expired && (
                                                <button
                                                    className="share-link-copy-btn"
                                                    onClick={() => onCopyShareUrl(link.token, link.id)}
                                                    title="Copy link"
                                                >
                                                    {copiedLinkId === link.id ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                                                </button>
                                            )}
                                            <button
                                                className="share-link-revoke-btn"
                                                onClick={() => onRevokeShareLink(link.id)}
                                                disabled={revokingShareLinkId === link.id}
                                                title="Delete link"
                                            >
                                                {revokingShareLinkId === link.id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Key Metrics Dashboard */}
            {key_metrics && (
                <div className="actionable-metrics">
                    {Object.entries(key_metrics).map(([key, metric]) => (
                        metric && metric.value && metric.value !== 'Unknown' && (
                            <div key={key} className="actionable-metric-card">
                                <div className="metric-info">
                                    <span className="metric-label">{metric.label}</span>
                                    <SentimentBadge 
                                        value={typeof metric.value === 'number' ? metric.value.toString() : metric.value} 
                                        type={key.includes('risk') ? 'risk' : key.includes('sentiment') ? 'sentiment' : 'sentiment'} 
                                    />
                                </div>
                                {metric.trend && (
                                    <span className={`metric-trend ${metric.trend.toLowerCase()}`}>
                                        {metric.trend === 'worsening' ? <TrendingDown size={14} /> : metric.trend === 'improving' ? <TrendingUp size={14} /> : <ArrowRight size={14} />} {metric.trend}
                                    </span>
                                )}
                            </div>
                        )
                    ))}
                </div>
            )}

            {hasMatrixItems && (
                <ReportSection title={<><Target size={16} /> Effort / Impact Matrix</>} icon="" className="actionable-section matrix-section">
                    <p className="section-description">Where each opportunity sits by the effort it takes versus the impact it delivers.</p>
                    <div className="qi-matrix">
                        <div className="qi-axis-y"><span>Impact → higher</span></div>
                        <div className="qi-grid">
                            {MATRIX_CELLS.map((cell) => {
                                const data = findMatrixQuadrant(cell.match);
                                const items = Array.isArray(data?.items) ? data.items : [];
                                return (
                                    <div key={cell.key} className={`qi-cell ${cell.tone}`}>
                                        <div className="qi-cell-head">
                                            <h4>{cell.label}</h4>
                                            <span className="qi-cell-axis">{cell.axis}</span>
                                        </div>
                                        {items.length > 0 ? (
                                            <ul className="qi-cell-items">
                                                {items.map((item, itemIdx) => (
                                                    <li key={itemIdx}>
                                                        <strong>{item.title}</strong>
                                                        {(item.opportunity_area || item.timeline) && (
                                                            <span>{[item.opportunity_area, item.timeline].filter(Boolean).join(' · ')}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="qi-cell-empty">—</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="qi-axis-x"><span>Effort → higher</span></div>
                    </div>
                </ReportSection>
            )}

            {/* Timeline - immediate actions, quick wins, and focus areas by horizon */}
            {timelineColumns.length > 0 && (
                <ReportSection title={<><CalendarClock size={16} /> Timeline</>} icon="" className="actionable-section timeline-section">
                    <p className="section-description">When to act. Each band is a time horizon; cards are the work that lands in that window.</p>
                    <div className="timeline-columns">
                        {timelineColumns.map((col) => (
                            <div key={col.key} className="timeline-column">
                                <div className="timeline-column-head">
                                    <h3>{col.label}</h3>
                                    <span className="timeline-column-count">{col.entries.length}</span>
                                </div>
                                <div className="timeline-column-cards">
                                    {col.entries.map((item, idx) => {
                                        const opportunityArea = item.opportunity_area || item.category;
                                        const quad = resolveQuadrantKey(item, matrixQuadrantLookup);
                                        const quadCell = quad ? MATRIX_CELLS.find(c => c.key === quad) : null;
                                        return (
                                            <div key={idx} className={`timeline-card ${quad ? 'tlq-' + quad : 'tlq-none'}`}>
                                                {quadCell && (
                                                    <div className="timeline-card-head">
                                                        <span className={`tl-quad-badge tlq-badge-${quad}`}>{quadCell.axis}</span>
                                                    </div>
                                                )}
                                                <h4>{item.title}</h4>
                                                {item.description && <p className="timeline-card-desc">{item.description}</p>}
                                                {item.capacity_implication && (
                                                    <p className="action-capacity-note"><TrendingDown size={13} /> {item.capacity_implication}</p>
                                                )}
                                                <div className="timeline-card-meta">
                                                    {opportunityArea && <span><FolderOpen size={12} /> {opportunityArea}</span>}
                                                    {item.suggested_owner && <span><User size={12} /> {item.suggested_owner}</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {strategicBets.length > 0 && (
                <ReportSection title={<><Lightbulb size={16} /> Bigger Strategic Bets</>} icon="" className="actionable-section strategic-bets-section">
                    <div className="action-items-list">
                        {strategicBets.map((bet, idx) => (
                            <div key={idx} className="action-item strategic-bet">
                                <div className="action-item-header">
                                    <h4>{bet.title}</h4>
                                    {(bet.opportunity_area || bet.category) && <span className="effort-badge">{bet.opportunity_area || bet.category}</span>}
                                    {bet.effort && <span className="effort-badge">Effort: {bet.effort}</span>}
                                    {bet.impact && <span className="impact-badge">Impact: {bet.impact}</span>}
                                    {bet.matrix_quadrant && <span className="effort-badge">{bet.matrix_quadrant}</span>}
                                </div>
                                <p className="action-description">{bet.description}</p>
                                <div className="action-meta">
                                    {bet.suggested_owner && <span><User size={14} /> {bet.suggested_owner}</span>}
                                    {bet.timeline && <span><Clock size={14} /> {bet.timeline}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {(department_org_design?.summary || orgDesignRecommendations.length > 0) && (
                <ReportSection title={<><Users size={16} /> Suggested Department Org Design</>} icon="" className="actionable-section org-design-section">
                    {department_org_design?.summary && <p className="section-description org-design-summary">{department_org_design.summary}</p>}
                    <div className="focus-areas-grid">
                        {orgDesignRecommendations.map((recommendation, idx) => (
                            <div key={idx} className="action-item org-design">
                                <div className="action-item-header">
                                    <h4>{recommendation.title}</h4>
                                    {(recommendation.opportunity_area || recommendation.category) && <span className="effort-badge">{recommendation.opportunity_area || recommendation.category}</span>}
                                    {recommendation.effort && <span className="effort-badge">Effort: {recommendation.effort}</span>}
                                    {recommendation.impact && <span className="impact-badge">Impact: {recommendation.impact}</span>}
                                </div>
                                <p className="action-description">{recommendation.description}</p>
                                <div className="action-meta">
                                    {recommendation.suggested_owner && <span><User size={14} /> {recommendation.suggested_owner}</span>}
                                    {recommendation.timeline && <span><Clock size={14} /> {recommendation.timeline}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Summary Stats */}
            {summary && (
                <div className="actionable-summary">
                    <div className="summary-stat">
                        <span className="summary-number">{summary.high_priority_count}</span>
                        <span className="summary-label">High Priority</span>
                    </div>
                    <div className="summary-stat">
                        <span className="summary-number">{summary.quick_win_count}</span>
                        <span className="summary-label">Quick Wins</span>
                    </div>
                    <div className="summary-stat">
                        <span className="summary-number">{summary.focus_area_count}</span>
                        <span className="summary-label">Focus Areas</span>
                    </div>
                </div>
            )}

            {/* Confidentiality Note */}
            <div className="actionable-footer">
                <p className="confidentiality-note">
                    <Lock size={14} /> {actionableReport.confidentiality_note}
                </p>
            </div>
        </div>
    );
}

/**
 * Department Sentiment View - Displays the CONDENSED sentiment report
 * This is a streamlined view showing top 3-5 highlights for leadership
 * No employee names or identifiable information
 */
function DepartmentSentimentView({ 
    department, 
    deptTrends, 
    sentimentReport, 
    condensedSummary,
    loadingCondensed,
    loadingSentiment, 
    showFullReport,
    onShowFullReport,
    onGenerateReport,
    showSharePanel,
    onToggleSharePanel,
    shareLinks,
    loadingShareLinks,
    generatingShareLink,
    shareLinkLabel,
    onShareLinkLabelChange,
    shareLinkExpiry,
    onShareLinkExpiryChange,
    shareRecipientEmails,
    onShareRecipientEmailsChange,
    shareLinkNotice,
    shareLinkError,
    revokingShareLinkId,
    onGenerateShareLink,
    onRevokeShareLink,
    onCopyShareUrl,
    copiedLinkId,
    showDepartmentSummarySharePanel,
    onToggleDepartmentSummarySharePanel,
    departmentSummaryShareLinks,
    loadingDepartmentSummaryShareLinks,
    generatingDepartmentSummaryShareLink,
    departmentSummaryShareLinkLabel,
    onDepartmentSummaryShareLinkLabelChange,
    departmentSummaryShareLinkExpiry,
    onDepartmentSummaryShareLinkExpiryChange,
    departmentSummaryShareRecipientEmails,
    onDepartmentSummaryShareRecipientEmailsChange,
    departmentSummaryShareLinkNotice,
    departmentSummaryShareLinkError,
    revokingDepartmentSummaryShareLinkId,
    onGenerateDepartmentSummaryShareLink,
    onRevokeDepartmentSummaryShareLink,
    onCopyDepartmentSummaryShareUrl,
    copiedDepartmentSummaryLinkId,
    departmentParticipants,
    loadingDepartmentParticipants,
    departmentParticipantsError,
    departmentParticipantsLoaded,
    selectedParticipantEmails,
    onFetchDepartmentParticipants,
    onToggleParticipantSelection,
    onSetAllParticipantsSelected,
    onPreviewDepartmentSummary,
    showDepartmentSummaryPreview,
    departmentSummaryPreviewData,
    loadingDepartmentSummaryPreview,
    departmentSummaryPreviewError,
    onCloseDepartmentSummaryPreview,
    generationLocked,
    generationLockMessage
}) {
    const analysis = sentimentReport?.analysis;

    // Loading state
    if (loadingSentiment || loadingCondensed) {
        return (
            <div className="leadership-loading">
                <div className="rp-spinner-large"></div>
                <h3>Generating Sentiment Report</h3>
                <p>AI is analyzing team sentiment and trends...</p>
                <p className="leadership-loading-note">This ensures no individual employee data appears in the report.</p>
            </div>
        );
    }

    // Error state
    if (sentimentReport?.error) {
        return (
            <ReportSection title="Sentiment Analysis" icon={<BarChart3 size={16} />}>
                <div className="leadership-error">
                    <span className="leadership-error-icon"><AlertTriangle size={16} /></span>
                    <h3>Unable to Generate Report</h3>
                    <p>{sentimentReport.error}</p>
                    <button className="rp-btn rp-btn-primary" onClick={onGenerateReport} disabled={generationLocked}>
                        <RefreshCw size={14} /> Try Again
                    </button>
                </div>
            </ReportSection>
        );
    }

    // No report yet - show prompt to generate
    if (!condensedSummary && !analysis) {
        return (
            <div className="rp-dept-view">
                <ReportSection title={`${department} Overview`} icon={<ClipboardList size={16} />}>
                    <div className="leadership-overview-grid">
                        <div className="leadership-stat-card">
                            <span className="leadership-stat-icon"><Users size={16} /></span>
                            <span className="leadership-stat-value">{deptTrends?.reports_generated || '—'}</span>
                            <span className="leadership-stat-label">Team Members Surveyed</span>
                        </div>
                    </div>
                </ReportSection>

                <ReportSection title="Generate Sentiment Report" icon={<Brain size={16} />}>
                    <div className="leadership-generate-prompt">
                        <div className="leadership-prompt-icon"><Lock size={32} /></div>
                        <h3>Ready to Generate Anonymized Report</h3>
                        <p>
                            Click below to generate a leadership-ready sentiment analysis. 
                            This report will contain <strong>no employee names</strong> or identifiable information.
                        </p>
                        <div className="leadership-features">
                            <div className="leadership-feature"><Check size={14} /> Team sentiment & morale indicators</div>
                            <div className="leadership-feature"><Check size={14} /> Workload health assessment</div>
                            <div className="leadership-feature"><Check size={14} /> Top recommendations</div>
                            <div className="leadership-feature"><Check size={14} /> Key risk areas</div>
                        </div>
                        {generationLocked && <p className="generate-note"><Lock size={14} /> {generationLockMessage}</p>}
                        <button className="rp-btn rp-btn-primary leadership-generate-btn" onClick={onGenerateReport} disabled={generationLocked}>
                            <Lock size={14} /> Generate Anonymized Report
                        </button>
                    </div>
                </ReportSection>
            </div>
        );
    }

    // Build summary from condensed data or fall back to analysis
    const summary = condensedSummary || (analysis ? buildSummaryFromAnalysis(analysis, department) : null);
    
    if (!summary) {
        return (
            <div className="leadership-loading">
                <div className="rp-spinner-large"></div>
                <p>Loading report...</p>
            </div>
        );
    }

    // Render the condensed sentiment report (main leadership view)
    return (
        <div className="rp-dept-view leadership-sentiment-report">
            {/* Report Header */}
            <div className="leadership-report-header">
                <div className="leadership-report-title">
                    <h2><Lock size={16} /> {department} Sentiment Report</h2>
                    <p className="leadership-report-subtitle">
                        Based on {sentimentReport?.respondent_count || deptTrends?.reports_generated || 'multiple'} anonymous survey responses
                    </p>
                </div>
                <div className="leadership-report-header-actions">
                    <button className={`rp-btn rp-btn-secondary share-btn ${showDepartmentSummarySharePanel ? 'active' : ''}`}
                        onClick={onToggleDepartmentSummarySharePanel}
                        title="Email employee-facing department summary invites"
                    >
                        <Mail size={14} /> Email Summary
                    </button>
                    <button className={`rp-btn rp-btn-secondary share-btn ${showSharePanel ? 'active' : ''}`}
                        onClick={onToggleSharePanel}
                        title="Share this report"
                    >
                        <Share2 size={14} /> Share
                    </button>
                    {sentimentReport?.generated_at && (
                        <div className="leadership-report-date">
                            Generated {new Date(sentimentReport.generated_at).toLocaleDateString()}
                        </div>
                    )}
                </div>
            </div>

            {/* Share Links Panel */}
            {showSharePanel && (
                <div className="share-panel">
                    <div className="share-panel-header">
                        <h3><Link size={16} /> Share Links for {department}</h3>
                        <button className="share-panel-close" onClick={onToggleSharePanel}>×</button>
                    </div>

                    {/* Generate New Link */}
                    <div className="share-generate-section">
                        <h4>Generate New Link</h4>
                        <div className="share-generate-form">
                            <div className="share-form-row">
                                <label className="share-field-group">
                                    <span>Label</span>
                                    <input
                                        type="text"
                                        className="share-input"
                                        placeholder="Optional, e.g. VP Operations"
                                        value={shareLinkLabel}
                                        onChange={(event) => onShareLinkLabelChange(event.target.value)}
                                    />
                                </label>
                                <label className="share-field-group share-expiry-field">
                                    <span>Expiration</span>
                                    <select
                                        className="share-select"
                                        value={shareLinkExpiry}
                                        onChange={(event) => onShareLinkExpiryChange(Number(event.target.value))}
                                    >
                                        <option value={7}>7 days</option>
                                        <option value={14}>14 days</option>
                                        <option value={30}>30 days</option>
                                        <option value={60}>60 days</option>
                                        <option value={90}>90 days</option>
                                    </select>
                                </label>
                            </div>
                            <label className="share-field-group share-email-field">
                                <span><Mail size={13} /> Email recipients</span>
                                <textarea
                                    className="share-input share-email-input"
                                    placeholder="name@example.com, another@example.com"
                                    value={shareRecipientEmails}
                                    onChange={(event) => onShareRecipientEmailsChange(event.target.value)}
                                    rows={3}
                                />
                            </label>
                            {shareLinkError && (
                                <div className="share-status share-status-error">
                                    <AlertTriangle size={14} /> {shareLinkError}
                                </div>
                            )}
                            {shareLinkNotice && (
                                <div className="share-status share-status-success">
                                    <CheckCircle2 size={14} /> {shareLinkNotice}
                                </div>
                            )}
                            <button
                                className="rp-btn rp-btn-primary share-generate-btn"
                                onClick={onGenerateShareLink}
                                disabled={generatingShareLink}
                            >
                                {generatingShareLink ? <><Loader2 size={14} className="spin" /> Generating...</> : <><ExternalLink size={14} /> Generate Link</>}
                            </button>
                        </div>
                    </div>

                    {/* Existing Links */}
                    <div className="share-links-section">
                        <h4>Existing Links</h4>
                        {loadingShareLinks ? (
                            <div className="share-links-loading"><Loader2 size={16} className="spin" /> Loading links...</div>
                        ) : shareLinks.length === 0 ? (
                            <p className="share-links-empty">No share links yet for this department.</p>
                        ) : (
                            <div className="share-links-list">
                                {shareLinks.map(link => (
                                    <div key={link.id} className={`share-link-item ${link.is_expired ? 'expired' : ''}`}>
                                        <div className="share-link-info">
                                            <div className="share-link-label">
                                                {link.label || 'Unnamed link'}
                                                {link.is_expired && <span className="share-link-expired-badge">Expired</span>}
                                            </div>
                                            <div className="share-link-meta">
                                                <span><Calendar size={12} /> {new Date(link.created_at).toLocaleDateString()}</span>
                                                {link.expires_at && <span><Clock size={12} /> Expires {new Date(link.expires_at).toLocaleDateString()}</span>}
                                                <span><Eye size={12} /> {link.access_count || 0} views</span>
                                            </div>
                                        </div>
                                        <div className="share-link-actions">
                                            {!link.is_expired && (
                                                <button
                                                    className="share-link-copy-btn"
                                                    onClick={() => onCopyShareUrl(link.token, link.id)}
                                                    title="Copy link"
                                                >
                                                    {copiedLinkId === link.id ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                                                </button>
                                            )}
                                            <button
                                                className="share-link-revoke-btn"
                                                onClick={() => onRevokeShareLink(link.id)}
                                                disabled={revokingShareLinkId === link.id}
                                                title="Delete link"
                                            >
                                                {revokingShareLinkId === link.id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showDepartmentSummarySharePanel && (
                <div className="share-panel">
                    <div className="share-panel-header">
                        <h3><Mail size={16} /> Employee Summary Invites for {department}</h3>
                        <button className="share-panel-close" onClick={onToggleDepartmentSummarySharePanel}>×</button>
                    </div>

                    <div className="share-generate-section">
                        <h4>Send Invite Link</h4>
                        <p className="share-links-empty">
                            Send employees a high-level department summary. Links expire no later than 90 days after creation.
                        </p>
                        <div className="share-generate-form">
                            <div className="dept-recipients-toolbar" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                <button
                                    type="button"
                                    className="rp-btn rp-btn-secondary"
                                    onClick={onFetchDepartmentParticipants}
                                    disabled={loadingDepartmentParticipants}
                                >
                                    {loadingDepartmentParticipants
                                        ? <><Loader2 size={14} className="spin" /> Pulling recipients...</>
                                        : <><Users size={14} /> Pull recipients for {department}</>}
                                </button>
                                <button
                                    type="button"
                                    className="rp-btn rp-btn-secondary"
                                    onClick={onPreviewDepartmentSummary}
                                    disabled={loadingDepartmentSummaryPreview}
                                >
                                    <Eye size={14} /> Preview what they'll see
                                </button>
                            </div>

                            {departmentParticipantsError && (
                                <div className="share-status share-status-error">
                                    <AlertTriangle size={14} /> {departmentParticipantsError}
                                </div>
                            )}

                            {departmentParticipantsLoaded && departmentParticipants.length > 0 && (
                                <div className="dept-recipients-list" style={{ border: '1px solid #d8e0e0', borderRadius: '8px', padding: '10px', marginBottom: '12px', maxHeight: '240px', overflowY: 'auto' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 600 }}>
                                            <UserCheck size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                            {selectedParticipantEmails.length} of {departmentParticipants.length} selected
                                        </span>
                                        <span style={{ display: 'flex', gap: '6px' }}>
                                            <button type="button" className="share-link-copy-btn" onClick={() => onSetAllParticipantsSelected(true)}>Select all</button>
                                            <button type="button" className="share-link-copy-btn" onClick={() => onSetAllParticipantsSelected(false)}>Clear</button>
                                        </span>
                                    </div>
                                    {departmentParticipants.map(participant => (
                                        <label
                                            key={participant.email}
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 4px', fontSize: '13px', cursor: 'pointer' }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedParticipantEmails.includes(participant.email)}
                                                onChange={() => onToggleParticipantSelection(participant.email)}
                                            />
                                            <span style={{ flex: 1 }}>
                                                <strong>{participant.name || participant.email}</strong>
                                                {participant.name && <span style={{ color: '#5a6b6b' }}> &lt;{participant.email}&gt;</span>}
                                                {participant.role_title && <span style={{ color: '#7a8a8a', display: 'block', fontSize: '12px' }}>{participant.role_title}</span>}
                                            </span>
                                            <span
                                                className={`share-link-expired-badge`}
                                                style={{
                                                    background: participant.completed ? '#e8f5e9' : '#fff3e0',
                                                    color: participant.completed ? '#2e7d32' : '#e65100',
                                                }}
                                            >
                                                {participant.completed ? 'Completed' : (participant.status || 'not started').replace(/_/g, ' ')}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            <div className="share-form-row">
                                <label className="share-field-group">
                                    <span>Label</span>
                                    <input
                                        type="text"
                                        className="share-input"
                                        placeholder="Optional, e.g. IT employee summary"
                                        value={departmentSummaryShareLinkLabel}
                                        onChange={(event) => onDepartmentSummaryShareLinkLabelChange(event.target.value)}
                                    />
                                </label>
                                <label className="share-field-group share-expiry-field">
                                    <span>Expiration</span>
                                    <select
                                        className="share-select"
                                        value={departmentSummaryShareLinkExpiry}
                                        onChange={(event) => onDepartmentSummaryShareLinkExpiryChange(Number(event.target.value))}
                                    >
                                        <option value={7}>7 days</option>
                                        <option value={14}>14 days</option>
                                        <option value={30}>30 days</option>
                                        <option value={60}>60 days</option>
                                        <option value={90}>90 days</option>
                                    </select>
                                </label>
                            </div>
                            <label className="share-field-group share-email-field">
                                <span><Mail size={13} /> Employee email recipients</span>
                                <textarea
                                    className="share-input share-email-input"
                                    placeholder="name@example.com, another@example.com"
                                    value={departmentSummaryShareRecipientEmails}
                                    onChange={(event) => onDepartmentSummaryShareRecipientEmailsChange(event.target.value)}
                                    rows={4}
                                />
                            </label>
                            {departmentSummaryShareLinkError && (
                                <div className="share-status share-status-error">
                                    <AlertTriangle size={14} /> {departmentSummaryShareLinkError}
                                </div>
                            )}
                            {departmentSummaryShareLinkNotice && (
                                <div className="share-status share-status-success">
                                    <CheckCircle2 size={14} /> {departmentSummaryShareLinkNotice}
                                </div>
                            )}
                            <button
                                className="rp-btn rp-btn-primary share-generate-btn"
                                onClick={onGenerateDepartmentSummaryShareLink}
                                disabled={generatingDepartmentSummaryShareLink}
                            >
                                {generatingDepartmentSummaryShareLink ? <><Loader2 size={14} className="spin" /> Sending...</> : <><Mail size={14} /> Send Invite</>}
                            </button>
                        </div>
                    </div>

                    <div className="share-links-section">
                        <h4>Existing Employee Summary Links</h4>
                        {loadingDepartmentSummaryShareLinks ? (
                            <div className="share-links-loading"><Loader2 size={16} className="spin" /> Loading links...</div>
                        ) : departmentSummaryShareLinks.length === 0 ? (
                            <p className="share-links-empty">No employee summary invite links yet for this department.</p>
                        ) : (
                            <div className="share-links-list">
                                {departmentSummaryShareLinks.map(link => (
                                    <div key={link.id} className={`share-link-item ${link.is_expired ? 'expired' : ''}`}>
                                        <div className="share-link-info">
                                            <div className="share-link-label">
                                                {link.label || 'Employee summary link'}
                                                {link.is_expired && <span className="share-link-expired-badge">Expired</span>}
                                            </div>
                                            <div className="share-link-meta">
                                                <span><Calendar size={12} /> {new Date(link.created_at).toLocaleDateString()}</span>
                                                {link.expires_at && <span><Clock size={12} /> Expires {new Date(link.expires_at).toLocaleDateString()}</span>}
                                                <span><Eye size={12} /> {link.access_count || 0} views</span>
                                            </div>
                                        </div>
                                        <div className="share-link-actions">
                                            {!link.is_expired && (
                                                <button
                                                    className="share-link-copy-btn"
                                                    onClick={() => onCopyDepartmentSummaryShareUrl(link.token, link.id)}
                                                    title="Copy link"
                                                >
                                                    {copiedDepartmentSummaryLinkId === link.id ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                                                </button>
                                            )}
                                            <button
                                                className="share-link-revoke-btn"
                                                onClick={() => onRevokeDepartmentSummaryShareLink(link.id)}
                                                disabled={revokingDepartmentSummaryShareLinkId === link.id}
                                                title="Delete link"
                                            >
                                                {revokingDepartmentSummaryShareLinkId === link.id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showDepartmentSummaryPreview && (
                <div
                    className="dept-summary-preview-overlay"
                    onClick={onCloseDepartmentSummaryPreview}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto' }}
                >
                    <div
                        className="dept-summary-preview-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: '#fff', borderRadius: '12px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e2e8e8' }}>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Eye size={16} /> Recipient preview &mdash; what employees will see
                            </strong>
                            <button className="share-panel-close" onClick={onCloseDepartmentSummaryPreview} aria-label="Close preview"><X size={18} /></button>
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {loadingDepartmentSummaryPreview ? (
                                <div className="share-links-loading" style={{ padding: '40px', textAlign: 'center' }}>
                                    <Loader2 size={20} className="spin" /> Loading preview...
                                </div>
                            ) : departmentSummaryPreviewError ? (
                                <div className="share-status share-status-error" style={{ margin: '18px' }}>
                                    <AlertTriangle size={14} /> {departmentSummaryPreviewError}
                                </div>
                            ) : departmentSummaryPreviewData ? (
                                <DepartmentSummaryPage previewData={departmentSummaryPreviewData} />
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* Headlines Card — top-of-page TLDR */}
            <HeadlinesCard
                analysis={analysis}
                summary={summary}
                department={department}
            />

            {/* Summary Text */}
            {summary.summary_text && (
                <div className="leadership-summary-text">
                    <p>{summary.summary_text}</p>
                </div>
            )}

            {/* Quick Stats Dashboard */}
            {summary.quick_stats && (
                <div className="leadership-quick-stats">
                    {summary.quick_stats.overall_sentiment && summary.quick_stats.overall_sentiment !== 'Unknown' && (
                        <div className="leadership-quick-stat">
                            <span className="stat-label">Overall Sentiment</span>
                            <SentimentBadge value={summary.quick_stats.overall_sentiment} type="sentiment" />
                        </div>
                    )}
                    {summary.quick_stats.burnout_risk && summary.quick_stats.burnout_risk !== 'Unknown' && (
                        <div className="leadership-quick-stat">
                            <span className="stat-label">Burnout Risk</span>
                            <SentimentBadge value={summary.quick_stats.burnout_risk} type="risk" />
                        </div>
                    )}
                    {summary.quick_stats.tool_satisfaction && summary.quick_stats.tool_satisfaction !== 'Unknown' && (
                        <div className="leadership-quick-stat">
                            <span className="stat-label">Tool Satisfaction</span>
                            <SentimentBadge value={summary.quick_stats.tool_satisfaction} type="sentiment" />
                        </div>
                    )}
                    {summary.quick_stats.process_efficiency && summary.quick_stats.process_efficiency !== 'Unknown' && (
                        <div className="leadership-quick-stat">
                            <span className="stat-label">Process Efficiency</span>
                            <SentimentBadge value={summary.quick_stats.process_efficiency} type="sentiment" />
                        </div>
                    )}
                </div>
            )}

            {/* Key Themes */}
            {summary.themes && summary.themes.length > 0 && (
                <ReportSection title="Key Themes" icon={<Tag size={16} />}>
                    <div className="leadership-themes-container">
                        {summary.themes.map((theme, idx) => (
                            <span key={idx} className="leadership-theme-tag">{theme}</span>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Top Highlights - The Main Content */}
            {summary.highlights && summary.highlights.length > 0 && (
                <ReportSection title="Key Findings" icon={<Pin size={16} />}>
                    <div className="leadership-highlights-list">
                        {summary.highlights.map((highlight, idx) => (
                            <div key={idx} className={`leadership-highlight-item ${highlight.status_type || 'default'}`}>
                                <div className="highlight-content">
                                    <div className="highlight-header-row">
                                        <h4>{highlight.category}</h4>
                                        <SentimentBadge value={highlight.status} type={highlight.status_type || 'sentiment'} />
                                    </div>
                                    <p className="highlight-detail-text">{highlight.detail}</p>
                                    {highlight.business_impact && (
                                        <p className="highlight-impact-text">
                                            <strong>Business Impact:</strong> {highlight.business_impact}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </ReportSection>
            )}

            {/* Report Footer */}
            <div className="leadership-report-footer">
                <p className="leadership-privacy-note">
                    <Lock size={14} /> This report contains no employee names or identifiable information
                </p>
            </div>
        </div>
    );
}

/**
 * Helper function to build summary from full analysis if condensed isn't available
 */
function buildSummaryFromAnalysis(analysis, department) {
    const highlights = [];
    
    // Team Sentiment
    if (analysis.team_sentiment) {
        highlights.push({
            category: "Team Sentiment",
            icon: <Smile size={16} />,
            status: analysis.team_sentiment.overall_assessment || "Unknown",
            detail: analysis.team_sentiment.morale_indicators || "No details available",
            status_type: "sentiment"
        });
    }
    
    // Workload
    if (analysis.workload_health) {
        highlights.push({
            category: "Workload Health",
            icon: <Scale size={16} />,
            status: analysis.workload_health.burnout_risk_level || analysis.workload_health.capacity_assessment || "Unknown",
            detail: analysis.workload_health.key_concerns?.[0] || analysis.workload_health.contributing_factors || "No concerns noted",
            status_type: "risk"
        });
    }
    
    // Top Recommendation
    if (analysis.strategic_recommendations?.length > 0) {
        const rec = analysis.strategic_recommendations[0];
        highlights.push({
            category: "Top Recommendation",
            icon: <Lightbulb size={16} />,
            status: rec.priority || "Action Item",
            detail: rec.recommendation,
            business_impact: rec.business_impact,
            status_type: "priority"
        });
    }
    
    // Top Risk
    if (analysis.risk_indicators?.length > 0) {
        const risk = analysis.risk_indicators[0];
        highlights.push({
            category: "Risk Alert",
            icon: <AlertTriangle size={16} />,
            status: risk.severity || "Monitor",
            detail: `${risk.risk_area}: ${risk.recommended_action}`,
            status_type: "risk"
        });
    }
    
    // Process
    if (analysis.process_themes?.top_friction_areas?.length > 0) {
        const friction = analysis.process_themes.top_friction_areas[0];
        highlights.push({
            category: "Process Friction",
            icon: <Settings size={16} />,
            status: analysis.process_themes.efficiency_rating || "Review",
            detail: `${friction.area}: ${friction.impact}`,
            status_type: "sentiment"
        });
    }
    
    return {
        department,
        highlights: highlights.slice(0, 5),
        summary_text: analysis.executive_summary || "",
        quick_stats: {
            overall_sentiment: analysis.team_sentiment?.overall_assessment || "Unknown",
            burnout_risk: analysis.workload_health?.burnout_risk_level || "Unknown",
            tool_satisfaction: analysis.technology_health?.overall_tool_satisfaction || "Unknown",
            process_efficiency: analysis.process_themes?.efficiency_rating || "Unknown"
        },
        themes: analysis.team_sentiment?.key_sentiment_themes || []
    };
}

export default LeadershipReportsPage;
